// src/pages/Dashboard.jsx – v4
// • Shows Bitcoin price + Sats stacked KPIs
// • Displays CURRENT recurring buy plan (amount & frequency)
// • Lists the 3 most‑recent trades (date, sats, USD)
// • Keeps cumulative‑sats / avg‑cost charts
// NOTE: endpoints rely on the same API paths used in Trades.jsx / BuyPlans.jsx

import React, { useEffect, useState, useMemo, useContext, useCallback, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  ScatterController,
  LineController,
  Filler,
} from "chart.js";
import {
  AlertTriangle,
  Clock,
  Gauge,
  WalletCards,
  ShieldCheck,
} from "lucide-react";
import api, {
  getAccessToken as getApiAccessToken,
  refreshAccessToken,
} from "../api";
import { Link, useNavigate } from "react-router-dom";
import { AppLauncher } from '@capacitor/app-launcher';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AuthContext } from "../context/AuthContext";
import { afTrack } from "../analytics/appsflyer";
import HuntingWindowCard from "../components/HuntingWindowCard";
import {
  getVerifiedWindowComparison,
  normalizedWindowKey,
} from "../utils/windowCloseComparison";
import { Share } from "@capacitor/share";
import {
  toUTCDate,
} from "../utils/smartTimingImpact";
import {
  groupTradesByWindow,
  fmtUsd2,
  fmtSats0,
} from "../utils/tradeWindows";

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, ScatterController, LineController, Filler);

const formatSignedPct = (value, digits = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00%";
  return `${n > 0 ? "+" : ""}${n.toFixed(digits)}%`;
};

const formatSignedSats = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n);
  return `${rounded > 0 ? "+" : ""}${rounded.toLocaleString()}`;
};

export default function Dashboard({ userInfo }) {
  
  const [summary, setSummary] = useState(null);
  const [btcPrice, setBtcPrice] = useState(null);
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [tradesLoading, setTradesLoading] = useState(true);
  const [trades, setTrades] = useState([]);
  const [strikeLinked, setStrikeLinked] = useState(() => {
    try {
      const raw = sessionStorage.getItem("strike_status_cache");
      if (raw) { const s = JSON.parse(raw); return s.linked === true ? true : null; }
    } catch {}
    return null;
  });
  const [strikeConnecting, setStrikeConnecting] = useState(false);
  const [showStrikeLinkWarning, setShowStrikeLinkWarning] = useState(false);
  const [strikeRetryPrompt, setStrikeRetryPrompt] = useState(false);
  const apiBase = import.meta.env.VITE_API_BASE_URL || "https://api.satstacker.app";
  const { token: ctxToken } = useContext(AuthContext);
  const jwt =
    getApiAccessToken() ||
    ctxToken ||
    "";
  const [canBuyNow, setCanBuyNow] = useState(null);
  const [fiatBuyable, setFiatBuyable] = useState(null);
  const [fiatAvailable, setFiatAvailable] = useState(null);
  const [fiatCurrency, setFiatCurrency] = useState(null);
  const [availableFiatCurrencies, setAvailableFiatCurrencies] = useState([]);
  const [buyingPowerReason, setBuyingPowerReason] = useState(null);
  const [allTrades, setAllTrades] = useState([]);
  const EPS = 0.005;
  const [showBTC, setShowBTC] = useState(() => {
    try { return localStorage.getItem("pref_show_btc") === "1"; } catch { return false; }
  });
  const [hideStack, setHideStack] = useState(() => {
    try { return localStorage.getItem("pref_hide_stack") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("pref_hide_stack", hideStack ? "1" : "0"); } catch {}
  }, [hideStack]);
  const [showChecklist, setShowChecklist] = useState(() => {
    try { return localStorage.getItem("dismiss_onboarding") !== "1"; } catch { return true; }
  });
  const dismissChecklist = () => {
    try { localStorage.setItem("dismiss_onboarding", "1"); } catch {}
    setShowChecklist(false);
  };
  const [smartTimingHighlights, setSmartTimingHighlights] = useState([]);
  const [smartTimingHighlightIndex, setSmartTimingHighlightIndex] = useState(0);
  const [smartTimingPerformance, setSmartTimingPerformance] = useState(null);
  const [smartTimingPerformanceLoaded, setSmartTimingPerformanceLoaded] = useState(false);
  const [referralStats, setReferralStats] = useState(null);
  const [referralStatsLoading, setReferralStatsLoading] = useState(false);
  const [
    pendingSmartTimingCheckout,
    setPendingSmartTimingCheckout,
  ] = useState(null);

  useEffect(() => {
    if (!userInfo?.id) {
      setPendingSmartTimingCheckout(null);
      return;
    }

    // Native purchases do not use the web
    // pending-checkout recovery flow.
    if (Capacitor.isNativePlatform()) {
      setPendingSmartTimingCheckout(null);
      return;
    }

    let cancelled = false;

    async function loadPendingSmartTimingCheckout() {
      try {
        const res = await api.get(
          "/me/smart-timing-checkout-pending"
        );

        if (!cancelled) {
          setPendingSmartTimingCheckout(
            res?.data?.pending || null
          );
        }
      } catch (e) {
        console.warn(
          "[Dashboard] could not load pending Smart Timing checkout",
          e
        );

        if (!cancelled) {
          setPendingSmartTimingCheckout(null);
        }
      }
    }

    loadPendingSmartTimingCheckout();

    const onFocus = () => {
      loadPendingSmartTimingCheckout();
    };

    window.addEventListener(
      "focus",
      onFocus
    );

    return () => {
      cancelled = true;

      window.removeEventListener(
        "focus",
        onFocus
      );
    };
  }, [userInfo?.id]);

  // ── Smart Timing nudge dismiss (reappears after 30 days) ─────────────────
  const NUDGE_KEY = "dismiss_smart_timing_nudge_ts";
  const NUDGE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  const [smartTimingNudgeDismissed, setSmartTimingNudgeDismissed] = useState(() => {
    try {
      const ts = parseInt(localStorage.getItem(NUDGE_KEY) || "0", 10);
      return ts > 0 && (Date.now() - ts) < NUDGE_TTL_MS;
    } catch { return false; }
  });
  const dismissSmartTimingNudge = (e) => {
    e.stopPropagation(); // don't navigate to /plans when tapping ×
    try { localStorage.setItem(NUDGE_KEY, String(Date.now())); } catch {}
    setSmartTimingNudgeDismissed(true);
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ── Subscription expiry warning ──────────────────────────────────────────
  // Compute how many days until sub expires (null if no expiry or not premium)
  const subExpiryWarning = useMemo(() => {
    if (!userInfo?.is_premium || !userInfo?.sub_expiry || !plan?.smart_timing) return null;
    const exp = new Date(userInfo.sub_expiry);
    if (isNaN(exp.getTime())) return null;
    const daysLeft = (exp - new Date()) / (1000 * 60 * 60 * 24);
    if (daysLeft <= 0) return null; // already expired — let the IAP flow handle it
    if (daysLeft <= 1)   return { level: "critical", daysLeft, exp };
    if (daysLeft <= 7)   return { level: "warning",  daysLeft, exp };
    if (daysLeft <= 14)  return { level: "notice",   daysLeft, exp };
    return null;
  }, [userInfo?.is_premium, userInfo?.sub_expiry, plan?.smart_timing]);

  // Dismiss key is tied to the expiry date so a new billing cycle won't re-show an old dismiss
  const subExpiryDismissKey = useMemo(() => {
    if (!userInfo?.sub_expiry) return null;
    const exp = new Date(userInfo.sub_expiry);
    if (isNaN(exp.getTime())) return null;
    return `dismiss_sub_expiry_${exp.toISOString().slice(0, 10)}`;
  }, [userInfo?.sub_expiry]);

  const [subExpiryDismissed, setSubExpiryDismissed] = useState(() => {
    try {
      const key = (() => {
        // can't call the memo here, so re-derive inline for initial state
        const expiry = null; // will be refreshed on first render
        return null;
      })();
      return false;
    } catch { return false; }
  });

  const PROD_DOWNLOAD = "https://satstacker.app/download";

  const buildShareUrl = (userInfo) => {
    const code = userInfo?.ref_code || userInfo?.referral_code || userInfo?.id || "";
    return `${PROD_DOWNLOAD}?ref=${encodeURIComponent(code)}`;
  };

  const shareUrl = useMemo(() => buildShareUrl(userInfo), [userInfo]);

  const allTradeWindows = useMemo(
    () => groupTradesByWindow(allTrades),
    [allTrades]
  );
  const recentTradeWindows = useMemo(
    () => allTradeWindows.slice(0, 3),
    [allTradeWindows]
  );

  // This is the most recent actually opened Smart Timing window that has not
  // yet reached its scheduled end. It is separate from the *next* plan window
  // and must never be treated as an unverifiable completed result.
  const activeUnfinishedWindow = useMemo(() => {
    const now = Date.now();
    return allTradeWindows.find((w) => {
      if (!w.isSmartTiming || !w.hasRealWindowStart ||
          (plan?.id != null && String(w.planId) !== String(plan.id))) return false;
      const start = toUTCDate(w.windowStart).getTime();
      const end = toUTCDate(w.windowEnd).getTime();
      return Number.isFinite(start) && Number.isFinite(end) &&
        start <= now && now < end;
    }) || null;
  }, [allTradeWindows, plan?.id]);

  const smartTimingWindowResults = useMemo(() => {
    const map = new Map();
    for (const result of smartTimingPerformance?.windows ||
      smartTimingPerformance?.recent_windows || []) {
      map.set(normalizedWindowKey(result?.plan_id, result?.window_start), result);
    }
    return map;
  }, [smartTimingPerformance]);

  // Reconcile independently against ALL recorded buys, not backend claims.
  const verifiedWindowComparisons = useMemo(() => {
    const map = new Map();
    for (const group of allTradeWindows) {
      const key = normalizedWindowKey(group.planId, group.windowStart);
      const verified = getVerifiedWindowComparison(
        group, smartTimingWindowResults.get(key)
      );
      if (verified) map.set(key, verified);
    }
    return map;
  }, [allTradeWindows, smartTimingWindowResults]);

  // Use the backend's canonical completed-window summary for the lifetime
  // Smart Timing result. This is the same number shown in Trade History.
  // Per-window cards can still use the stricter client-side verification above.
  const smartTimingSummary =
    smartTimingPerformance?.benchmark === "scheduled_window_end"
      ? (smartTimingPerformance?.summary || null)
      : null;

  // Only a real, already-ended window that failed reconciliation can support
  // a historical-verification notice. Pending windows are NOT counted here.
  const unverifiedCompletedWindowCount = useMemo(() => {
    const results = smartTimingPerformance?.windows;
    if (!Array.isArray(results)) return 0;
    const now = Date.now();
    return results.filter((result) => {
      const end = toUTCDate(result?.completed_at).getTime();
      return Number.isFinite(end) && end <= now &&
        !verifiedWindowComparisons.has(
          normalizedWindowKey(result?.plan_id, result?.window_start)
        );
    }).length;
  }, [smartTimingPerformance, verifiedWindowComparisons]);

  const shareSmartTimingImpact = async () => {
    if (!smartTimingSummary?.has_shareable_result) return;

    const pct = formatSignedPct(
      smartTimingSummary.gain_pct
    );

    const text =
      `My Smart Timing result is ${pct} additional sats vs scheduled DCA across ` +
      `${smartTimingSummary.completed_windows.toLocaleString()} completed buy windows on SatStacker ⚡\n` +
      `Same schedule. Same budget. Smarter timing.\n` +
      `Use my link and you'll get 2 months of Smart Timing free instead of the usual 1.`;

    try {
      afTrack("smart_timing_impact_shared", {
        smart_timing_gain_pct: Number(
          Number(smartTimingSummary.gain_pct || 0).toFixed(2)
        ),
        completed_windows:
          smartTimingSummary.completed_windows || 0,
      });

      if (Capacitor.isNativePlatform()) {
        await Share.share({
          title: "SatStacker Smart Timing",
          text,
          url: shareUrl,
        });
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: "SatStacker Smart Timing",
          text,
          url: shareUrl,
        });
        return;
      }

      await navigator.clipboard.writeText(
        `${text}\n${shareUrl}`
      );
      alert("Copied share text to clipboard.");
    } catch (e) {
      const msg = String(
        e?.message || ""
      ).toLowerCase();

      if (!msg.includes("cancel")) {
        console.warn(
          "Impact share failed",
          e
        );
      }
    }
  };

  // Re-check dismiss state whenever the key changes
  useEffect(() => {
    if (!subExpiryDismissKey) return;
    try {
      setSubExpiryDismissed(localStorage.getItem(subExpiryDismissKey) === "1");
    } catch {}
  }, [subExpiryDismissKey]);

  const [localPlanPreviewState, setLocalPlanPreviewState] = useState(null);

  const dismissSubExpiryWarning = () => {
    if (subExpiryDismissKey) {
      try { localStorage.setItem(subExpiryDismissKey, "1"); } catch {}
    }
    setSubExpiryDismissed(true);
  };
  // ────────────────────────────────────────────────────────────────────────
  // --- Logged-out Smart Timing preview
  const PREVIEW_WEEKS = 4;
  const PREVIEW_WEEKLY_USD = 100;
  const PREVIEW_TOTAL_USD = PREVIEW_WEEKS * PREVIEW_WEEKLY_USD; // 400

  function readCachedDisplayBalance(maxAgeMs = 10 * 60 * 1000) {
    try {
      const raw = sessionStorage.getItem("strike_display_balance_v2");
      if (!raw) return null;
      const { v, ts } = JSON.parse(raw);
      if (typeof v !== "number" || !Number.isFinite(v)) return null;
      if (!ts || (Date.now() - ts) > maxAgeMs) return null;
      return v;
    } catch { return null; }
  }
  const [displayBalance, setDisplayBalance] = useState(() => readCachedDisplayBalance());
  const [strikeError, setStrikeError] = useState(null);

  const planAmountFiat = useMemo(() => {
    if (!plan) return 0;

    const raw =
      plan.amount_fiat ??
      plan.amount_usd ??
      0;

    const amount = Number(raw);

    return Number.isFinite(amount) ? amount : 0;
  }, [plan]);

    // How much USD we still need to run all *pending* tranches (exec === false)
  const pendingFrac = useMemo(() => {
    if (!plan) return 0;

    // Normalize tranches to an array (API may return a JSON string between windows)
    let trs = plan.tranches;
    if (typeof trs === "string") {
      try { trs = JSON.parse(trs); } catch { trs = null; }
    }

    if (Array.isArray(trs) && trs.length) {
      const s = trs.reduce((sum, t) => {
        // exec: true means already fired — skip it. Only count exec: false/null/undefined.
        const f = Number(t?.frac ?? 0);
        return (t?.exec !== true) ? (sum + (Number.isFinite(f) ? f : 0)) : sum;
      }, 0);
      return Math.max(0, Math.min(1, s)); // clamp to [0,1]
    }

    // Fallback: if tranches aren't available, derive pending fraction from
    // unspent_fiat_usd vs amount_usd so a partially-executed window doesn't
    // show the full amount as "needed".
    const amt = planAmountFiat;

    const unspent = Number(
      plan.unspent_fiat ??
      plan.unspent_fiat_usd ??
      amt,
    );
    if (amt > 0 && Number.isFinite(unspent)) {
      return Math.max(0, Math.min(1, unspent / amt));
    }

    // Last resort: assume fully pending (new window, no data yet)
    return 1;
  }, [plan]);

  // Displayed “needed” amount (no extra headroom)
  const neededFiat = useMemo(() => {
    if (!plan) return null;

    const need = planAmountFiat * pendingFrac;

    return Math.max(
      0,
      Number.isFinite(need) ? need : 0,
    );
  }, [plan, planAmountFiat, pendingFrac]);

  const requiredFiat = useMemo(() => {
    if (!plan) return null;

    const need = planAmountFiat * pendingFrac;

    return Math.max(
      0,
      Number.isFinite(need) ? need : 0,
    );
  }, [plan, planAmountFiat, pendingFrac]);

  const decisionReady = useMemo(() => {
    const linkedKnown =
      strikeLinked === true || strikeLinked === false;

    const currencyProblem =
      buyingPowerReason === "unsupported_funding_currency" ||
      buyingPowerReason === "funding_currency_required";

    const haveProbe =
      canBuyNow === true ||
      canBuyNow === false ||
      Number.isFinite(fiatBuyable);

    const haveBalance =
      typeof fiatAvailable === "number" ||
      typeof displayBalance === "number";

    return (
      linkedKnown &&
      (currencyProblem || haveProbe || haveBalance || plan)
    );
  }, [
    strikeLinked,
    canBuyNow,
    fiatBuyable,
    fiatAvailable,
    displayBalance,
    buyingPowerReason,
    plan,
  ]);


  const needsTopUp = useMemo(() => {
    if (!userInfo?.id || !plan) return false;
    if (strikeLinked !== true) return false;
    if (!decisionReady) return false;

    // A currency-selection/configuration problem is not a funding problem.
    if (
      buyingPowerReason === "unsupported_funding_currency" ||
      buyingPowerReason === "funding_currency_required"
    ) {
      return false;
    }

    if (neededFiat == null || !Number.isFinite(neededFiat)) {
      return false;
    }

    const needed = Number(neededFiat);

    if (typeof displayBalance === "number") {
      return (displayBalance + EPS) < needed;
    }

    const byProbe = Number.isFinite(fiatBuyable)
      ? (fiatBuyable + EPS) < needed
      : canBuyNow === false;

    const byBalance =
      typeof fiatAvailable === "number"
        ? (fiatAvailable + EPS) < needed
        : false;

    return byProbe || byBalance;
  }, [
    userInfo?.id,
    plan,
    strikeLinked,
    decisionReady,
    buyingPowerReason,
    neededFiat,
    displayBalance,
    canBuyNow,
    fiatBuyable,
    fiatAvailable,
  ]);

  const stepPlanDone    = !!plan;
  const stepStrikeDone  = strikeLinked === true;
  const hasCompletedBuy = allTrades.length > 0;

  const shouldShowSmartTimingUpsell =
    !!plan &&
    plan.smart_timing !== true &&
    !userInfo?.is_premium &&
    hasCompletedBuy &&
    !smartTimingNudgeDismissed;
  const stepFirstBuy =
    (trades?.length > 0) ||
    (
      !!plan &&
      strikeLinked === true &&
      decisionReady &&
      !needsTopUp
    );

  const steps = [
    {
      id: "plan",
      label: "Create your Buy Plan",
      done: stepPlanDone,
      actionLabel: "Create Plan",
      action: () => navigate("/plans"),
    },
    {
      id: "strike",
      label: stepPlanDone ? "Connect Strike to activate your plan" : "Connect Strike",
      done: stepStrikeDone,
      actionLabel: stepPlanDone ? "Activate" : "Connect",
      action: () => setShowStrikeLinkWarning(true),
    },
    {
      id: "firstbuy",
      label: "Fund & run your first buy",
      done: stepFirstBuy,
      actionLabel: "Open Strike",
      action: openStrike,
    },
  ];

  const completed = steps.filter(s => s.done).length;
  const total = steps.length;
  const pct = Math.round((completed / total) * 100);

  const prevStrikeLinked = useRef(null);

  useEffect(() => {
    if (prevStrikeLinked.current !== true && strikeLinked === true) {
      afTrack("strike_link_success", { entry: "dashboard" });
    }
    prevStrikeLinked.current = strikeLinked;
  }, [strikeLinked]);

  const didTrackView = useRef(false);

  const [calcBanner, setCalcBanner] = useState(() => {
    try {
      const raw = localStorage.getItem("calc_prefill");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  // ── Bitcoin education sheet ───────────────────────────────────────────────
  const [showEduSheet, setShowEduSheet] = useState(false);
  const [eduSlide, setEduSlide] = useState(0);
  const eduTimerRef = useRef(null);

  // Show after 8s of inactivity: new user (no trades, no plan), never seen before
  // Cancels if the user taps a checklist button — they already know what they're doing
  useEffect(() => {
    if (planLoading || tradesLoading) return;
    const alreadySeen = (() => { try { return localStorage.getItem("edu_sheet_seen") === "1"; } catch { return false; } })();
    if (alreadySeen) return;
    if (plan || trades.length > 0) return;

    let fired = false;
    const cancel = () => { if (!fired) clearTimeout(eduTimerRef.current); };

    // Only cancel on navigation/checklist taps, not passive scrolling
    // Use capture phase to catch any button tap before it bubbles
    const onTap = (e) => {
      if (e.target?.closest("button") || e.target?.closest("a")) cancel();
    };
    window.addEventListener("touchstart", onTap);
    window.addEventListener("mousedown", onTap);

    eduTimerRef.current = setTimeout(() => {
      fired = true;
      setShowEduSheet(true);
      try { localStorage.setItem("edu_sheet_seen", "1"); } catch {}
      afTrack("edu_sheet_shown", {});
    }, 8000);

    return () => {
      clearTimeout(eduTimerRef.current);
      window.removeEventListener("touchstart", onTap);
      window.removeEventListener("mousedown", onTap);
    };
  }, [planLoading, tradesLoading, plan, trades.length]);

  const dismissEduSheet = () => {
    setShowEduSheet(false);
    setEduSlide(0);
    afTrack("edu_sheet_dismissed", { slide: eduSlide });
  };

  const EDU_SLIDES = [
    {
      icon: <span className="text-4xl font-black text-orange-400 leading-none">₿</span>,
      title: "What is Bitcoin?",
      body: "Bitcoin is digital money that no government or bank controls. There will only ever be 21 million, making it scarce by design. Many people buy it as a long-term store of value.",
    },
    {
      icon: (
        <svg width="56" height="56" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 20 V15 C14 9 30 9 30 15 V20" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
          <rect x="9" y="20" width="26" height="18" rx="4" stroke="#f97316" strokeWidth="2.5"/>
          <circle cx="22" cy="29" r="3" stroke="#f97316" strokeWidth="2"/>
          <line x1="22" y1="32" x2="22" y2="34" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      title: "Why Bitcoin?",
      body: "Traditional currencies can be inflated by governments and banks at any time. Bitcoin has a fixed supply that nobody can change. It gives anyone in the world a way to save and transact outside the traditional financial system.",
    },
    {
      icon: (
        <span className="inline-flex items-end gap-1 leading-none">
          {[10, 14, 18, 22, 26].map((h, i) => (
            <span
              key={i}
              className="inline-block w-3 rounded-sm bg-orange-400"
              style={{ height: `${h}px`, opacity: 0.5 + i * 0.12 }}
            />
          ))}
        </span>
      ),
      title: "What is DCA?",
      body: "Dollar Cost Averaging (DCA) means buying a fixed amount on a regular schedule: daily, weekly, or bi-weekly. It removes the stress of timing the market and smooths out your average purchase price over time.",
    },
    {
      icon: <span className="text-4xl leading-none">🟠</span>,
      title: "What is a sat?",
      body: "A sat (short for satoshi) is the smallest unit of Bitcoin, like a penny to a dollar. One Bitcoin equals 100 million sats. SatStacker tracks your stack in sats so you can see every fraction you earn, even when buying small amounts.",
    },
    {
      icon: <span className="text-4xl leading-none">⚡</span>,
      title: "How SatStacker helps",
      body: "SatStacker automates your Bitcoin DCA through Strike. Smart Timing — SatStacker's buying algorithm — looks for better Bitcoin prices inside each buy window while keeping your regular budget and cadence. Same schedule. Same budget. Smarter entries.",
    },
  ];

  const startSmartTimingSignup = (entry = "dashboard_logged_out") => {
    afTrack("signup_cta_tapped", {
      entry,
      variant: "start_smart_timing_trial",
      platform: Capacitor.isNativePlatform()
        ? Capacitor.getPlatform()
        : "web",
      source_screen: "first_open_dashboard",
    });

    // Preserve intent so Signup can send the user toward
    // Smart Timing plan creation after account creation.
    try {
      localStorage.setItem(
        "satstacker_signup_intent",
        JSON.stringify({
          intent: "smart_timing_trial",
          return_to: "/plans?smartTiming=1",
          ts: Date.now(),
        }),
      );
    } catch {}

    navigate(
      `/signup?redirect=${encodeURIComponent("/plans?smartTiming=1")}`
    );
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ── Pre-auth education popup ──────────────────────────────────────────────
  const [showPreAuthEdu, setShowPreAuthEdu] = useState(false);
  const [preAuthStep, setPreAuthStep] = useState("ask_btc"); 
  // steps: "ask_btc" | "ask_dca" | "learn_btc" | "learn_dca" | "learn_satstacker" | "done"
  const preAuthTimerRef = useRef(null);

  useEffect(() => {
    if (userInfo?.id) return; // signed-in users get the other popup
    const alreadySeen = (() => {
      try { return localStorage.getItem("preauth_edu_seen") === "1"; } catch { return false; }
    })();
    if (alreadySeen) return;

    let fired = false;
    const cancel = () => { if (!fired) clearTimeout(preAuthTimerRef.current); };
    const onTap = (e) => {
      if (e.target?.closest("button") || e.target?.closest("a")) cancel();
    };
    window.addEventListener("touchstart", onTap);
    window.addEventListener("mousedown", onTap);

    preAuthTimerRef.current = setTimeout(() => {
      fired = true;
      setShowPreAuthEdu(true);
      try { localStorage.setItem("preauth_edu_seen", "1"); } catch {}
      afTrack("preauth_edu_shown", {});
    }, 30000);

    return () => {
      clearTimeout(preAuthTimerRef.current);
      window.removeEventListener("touchstart", onTap);
      window.removeEventListener("mousedown", onTap);
    };
  }, [userInfo?.id]);

  const dismissPreAuthEdu = () => {
    setShowPreAuthEdu(false);
    setPreAuthStep("ask_btc");
    afTrack("preauth_edu_dismissed", { step: preAuthStep });
  };
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (didTrackView.current) return;

    afTrack("dashboard_view", {
      entry: "dashboard",
      logged_in: !!userInfo?.id,
    });

    didTrackView.current = true;
  }, [userInfo?.id]);

  const didTrackChecklistShown = useRef(false);

  useEffect(() => {
    if (!userInfo?.id) return;
    if (!showChecklist) return;
    if (didTrackChecklistShown.current) return;

    afTrack("onboarding_checklist_shown", {
      entry: "dashboard",
      completed,
      total,
    });

    didTrackChecklistShown.current = true;
  }, [userInfo?.id, showChecklist, completed, total]);

  const prevPlanDone = useRef(false);
  const prevStrikeDone = useRef(false);
  const prevFirstBuyDone = useRef(false);

  useEffect(() => {
    // plan completion (plan appeared)
    if (!prevPlanDone.current && stepPlanDone) {
      afTrack("onboarding_step_complete", { entry: "dashboard", step_id: "plan" });
    }
    prevPlanDone.current = stepPlanDone;

    // strike completion is already tracked with strike_link_success,
    // but it's still helpful to also track funnel completion uniformly:
    if (!prevStrikeDone.current && stepStrikeDone) {
      afTrack("onboarding_step_complete", { entry: "dashboard", step_id: "strike" });
    }
    prevStrikeDone.current = stepStrikeDone;

    // first buy completion (either trade exists OR balance sufficient and ready)
    if (!prevFirstBuyDone.current && stepFirstBuy) {
      afTrack("onboarding_step_complete", { entry: "dashboard", step_id: "firstbuy" });
    }
    prevFirstBuyDone.current = stepFirstBuy;
  }, [stepPlanDone, stepStrikeDone, stepFirstBuy]);

  useEffect(() => {
    try { localStorage.setItem("pref_show_btc", showBTC ? "1" : "0"); } catch {}
  }, [showBTC]);

  // seed to sats if missing
  useEffect(() => {
    try {
      if (localStorage.getItem("pref_show_btc") == null) {
        localStorage.setItem("pref_show_btc", "0");
        setShowBTC(false);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!userInfo?.id || !jwt) return;

    let cancelled = false;

    const fetchReferralStats = async () => {
      setReferralStatsLoading(true);

      try {
        const res = await api.get("/me/referrals", {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        });

        if (!cancelled) {
          setReferralStats(res.data || null);
        }
      } catch (err) {
        console.warn("Referral stats unavailable", err);
      } finally {
        if (!cancelled) {
          setReferralStatsLoading(false);
        }
      }
    };

    fetchReferralStats();

    return () => {
      cancelled = true;
    };
  }, [userInfo?.id, jwt]);

  // make unit pref per-user
  useEffect(() => {
    if (!userInfo?.id) return;
    try {
      const last = localStorage.getItem("unit_last_user");
      if (last !== String(userInfo.id)) {
        localStorage.setItem("unit_last_user", String(userInfo.id));
        localStorage.setItem("pref_show_btc", "0");
        setShowBTC(false);
      }
    } catch {}
  }, [userInfo?.id]);

  useEffect(() => {
    const onUpdate = () => {
      const v = readCachedDisplayBalance();
      if (typeof v === "number") setDisplayBalance(v);
    };
    window.addEventListener("strike-balance-updated", onUpdate);
    return () => window.removeEventListener("strike-balance-updated", onUpdate);
  }, []);

  useEffect(() => {
    if (!userInfo?.id) return;
    try {
      const last = localStorage.getItem("onboarding_last_user");
      if (last !== String(userInfo.id)) {
        localStorage.removeItem("dismiss_onboarding");
        localStorage.setItem("onboarding_last_user", String(userInfo.id));
      }
    } catch {}
  }, [userInfo?.id]);

  useEffect(() => {

    let cancelled = false;

    async function loadSmartTimingHighlights() {
      try {
        const res = await api.get(
          "/smart-timing/highlights"
        );

        if (cancelled) return;

        const items = Array.isArray(
          res.data?.highlights
        )
          ? res.data.highlights.filter((h) => {
              if (h?.benchmark !== "scheduled_window_end" ||
                  !h.window_end_source ||
                  h.window_end_source === "current_freq_fallback") return false;
              const end = toUTCDate(h.completed_at).getTime();
              const quote = toUTCDate(h.scheduled_price_at).getTime();
              return Number(h.more_btc_pct) >= 2 && Number(h.extra_sats) > 0 &&
                Number.isFinite(end) && Number.isFinite(quote) &&
                quote >= end && quote <= end + 15 * 60000;
            })
          : [];

        setSmartTimingHighlights(items);

        if (items.length > 0) {
          // Rotate the starting highlight once per UTC day.
          // It stays stable during the day instead of changing
          // randomly every time the dashboard reloads.
          const dayNumber = Math.floor(
            Date.now() / 86400000
          );

          setSmartTimingHighlightIndex(
            dayNumber % items.length
          );

          afTrack("smart_timing_highlights_loaded", {
            count: items.length,
            logged_in: !!userInfo?.id,
          });
        }
      } catch (err) {
        console.warn(
          "[Dashboard] Smart Timing highlights unavailable",
          err
        );

        if (!cancelled) {
          setSmartTimingHighlights([]);
        }
      }
    }

    loadSmartTimingHighlights();

    return () => {
      cancelled = true;
    };
  }, []);

  const formatHighlightPrice = (value) => {
    const n = Number(value);

    if (!Number.isFinite(n)) return "--";

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);
  };

  const formatHighlightDate = (value) => {
    if (!value) return "";

    const d = new Date(value);

    if (Number.isNaN(d.getTime())) return "";

    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  };


  const formatHighlightSats = (value) => {
    const n = Number(value);

    if (!Number.isFinite(n)) return "--";

    return Math.round(n).toLocaleString();
  };

  const currentSmartTimingHighlight =
    smartTimingHighlights.length > 0
      ? smartTimingHighlights[
          smartTimingHighlightIndex %
            smartTimingHighlights.length
        ]
      : null;

  function parseApiDateUtc(value) {
    if (!value) return null;

    if (value instanceof Date) return value;

    if (typeof value !== "string") {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const s = value.trim();

    const hasTimezone =
      /Z$/i.test(s) ||
      /[+-]\d{2}:\d{2}$/.test(s) ||
      /[+-]\d{4}$/.test(s);

    const d = new Date(hasTimezone ? s : `${s}Z`);

    return Number.isNaN(d.getTime()) ? null : d;
  }

  function nextWindowStart(freq, windowStartIso) {
    if (!freq || !windowStartIso) return null;

    const start = parseApiDateUtc(windowStartIso);
    if (!start) return null;

    const d = new Date(start);
    if (freq === "daily")      d.setDate(d.getDate() + 1);
    else if (freq === "weekly") d.setDate(d.getDate() + 7);
    else if (freq === "bi-weekly") d.setDate(d.getDate() + 14);
    else return null;
    return d;
  }

  function windowDays(freq) {
    if (!freq) return 14;

    const f = String(freq).toLowerCase();

    if (f === "daily") return 1;
    if (f === "weekly") return 7;

    return 14; // bi-weekly default
  }

  function fmtDate(dt) {
    if (!dt) return "";
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    }).format(dt);
  }

  function formatFiat(amount, currency = "USD") {
    const value = Number(amount);

    if (!Number.isFinite(value)) {
      return "--";
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `${currency || "USD"} ${value.toFixed(2)}`;
    }
  }

  function fmtStackValue(totalSats, showBTC, hideStack) {
    const sats = Number(totalSats || 0);

    if (hideStack) {
      return showBTC ? "•••••••• BTC" : "•••••• sats";
    }

    if (showBTC) {
      return `${(sats / 100_000_000).toFixed(8)} BTC`;
    }

    return `${Math.round(sats).toLocaleString()} sats`;
  }

  const isAndroid = /Android/i.test(navigator.userAgent);
  async function openStrike() {
    try {
      afTrack("strike_open_clicked", {
        entry: "dashboard",
        reason: "fund_or_first_buy",
      });
      if (Capacitor.isNativePlatform()) {
        // 1) Try custom scheme (works only if Strike registered one on the device)
        const scheme = 'strike://'; // If Strike doesn’t support this, we’ll fall back.
        const can = await AppLauncher.canOpenUrl({ url: scheme });
        if (can.value) {
          await AppLauncher.openUrl({ url: scheme });
          return;
        }

        // 2) Android: try opening the Play app directly (market://)
        if (Capacitor.getPlatform() === 'android') {
          await AppLauncher.openUrl({ url: 'market://details?id=zapsolutions.strike' });
          return;
        }

        // 3) iOS: App Store page
        await AppLauncher.openUrl({ url: 'https://apps.apple.com/us/app/strike-bitcoin/id1488724463' });
        return;
      }
    } catch (e) {
      console.warn('openStrike failed, falling back', e);
    }
    // 4) Web/Desktop fallback if not native or everything else fails
    window.open('https://strike.me/en', '_blank', 'noopener,noreferrer');
  }
  
  const openStrikeStore = async () => {
    // ✅ mark intent before leaving the app
    try { localStorage.setItem("strike_store_click_ts", String(Date.now())); } catch {}

    afTrack("strike_store_clicked", {
      entry: "dashboard",
      platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web",
    });

    const IOS_ITMS = 'itms-apps://itunes.apple.com/app/id1488724463';
    const IOS_HTTP = 'https://apps.apple.com/us/app/strike-bitcoin/id1488724463';
    const ANDROID_MARKET = 'market://details?id=zapsolutions.strike';
    const ANDROID_HTTP = 'https://play.google.com/store/apps/details?id=zapsolutions.strike';

    try {
      if (Capacitor.isNativePlatform()) {
        const platform = Capacitor.getPlatform();
        if (platform === 'ios') {
          // Skip canOpenUrl — directly attempt itms-apps://, fall back to https
          try {
            await AppLauncher.openUrl({ url: IOS_ITMS });
          } catch {
            await AppLauncher.openUrl({ url: IOS_HTTP });
          }
        } else {
          // Android: skip canOpenUrl — directly attempt market://, fall back to https
          try {
            await AppLauncher.openUrl({ url: ANDROID_MARKET });
          } catch {
            await AppLauncher.openUrl({ url: ANDROID_HTTP });
          }
        }
      } else {
        window.open('https://strike.me/en', '_blank', 'noopener,noreferrer');
      }
    } catch {
      window.location.href = IOS_HTTP;
    }
  };

  const [loadingProbe, setLoadingProbe] = useState(false);
  const [loadingBalances, setLoadingBalances] = useState(false);

  function getAccessToken(ctxToken) {
      return (
      getApiAccessToken() ||
      (typeof ctxToken === "string"
        ? ctxToken
        : "")
    );
  }

  async function handleConnectStrike() {
    try {
      afTrack("strike_link_started", { entry: "dashboard" });
      // 1) Silent refresh to update access token
      try {
        await refreshAccessToken();
      } catch {}

      // 2) Grab the freshest access token
      const access = getAccessToken(ctxToken);
      if (typeof access !== "string" || !access.length) {
        // remember intent and go to login
        localStorage.setItem("postLoginAction", "connectStrike");
        window.location.href = "/login";
        return;
      }

      // 3) Ask backend for a short-lived link token (valid ~5 min)
      const ltRes = await fetch(`${apiBase}/oauth/strike/link-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${access}` },
        credentials: "include",
      });
      if (!ltRes.ok) throw new Error(`link-token failed (${ltRes.status})`);
      const { token: linkToken } = await ltRes.json();
      if (!linkToken) throw new Error("No link token");

      afTrack("strike_oauth_launched", { entry: "dashboard" });

      // Mark intent so onReturnFromExternal knows this was a Strike OAuth attempt
      try { localStorage.setItem("strike_oauth_intent_ts", String(Date.now())); } catch {}

      // 4) Kick off OAuth
      const url = `${apiBase}/oauth/strike/login?t=${encodeURIComponent(
        linkToken
      )}`;
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url });
      } else {
        window.location.href = url;
      }
    } catch (e) {
      console.error(e);
      setStrikeError("Could not start Strike connection. Please try again.");
    }
  }

  async function withAuthRetry(fn) {
    try {
      return await fn();
    } catch (e) {
      const status = e?.response?.status;

      if (status === 401) {
        try {
          await refreshAccessToken();
        } catch {}

        return await fn(true);
      }

      throw e;
    }
  }

  /* ──────────────────────────────────────────────────────────
     initial load once we know the user ID
  ────────────────────────────────────────────────────────── */
  // always fetch BTC price on load
  useEffect(() => {
    fetchPrice();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await refreshAccessToken();
      } catch {}

      checkStrikeLinked();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // only fetch user-dependent stuff if logged in
  useEffect(() => {
    if (!userInfo?.id) {
      setSummary(null);
      setPlan(null);
      setTrades([]);
      setAllTrades([]);
      setSmartTimingPerformance(null);
      setSmartTimingPerformanceLoaded(false);
      setStrikeLinked(null);
      return;
    }

    fetchSummary();
    fetchPlan();
    fetchTrades();
    fetchSmartTimingPerformance();
    fetchPlan();
  }, [userInfo]);

  useEffect(() => {
    const doIt = new URLSearchParams(window.location.search).get("do");
    if (doIt === "strike-connect") {
      // small timeout lets context hydrate before we read tokens
      setTimeout(() => handleConnectStrike(), 0);
    }
  }, []);

  /* ---------- data fetch helpers ---------- */
  const fetchSummary = async () => {
    try {
      const { data } = await api.get(`/users/${userInfo.id}/summary`);
      setSummary(data);
    } catch (err) {
      console.error("Error fetching summary", err);
    }
  };

  const fetchPrice = async () => {
    try {
      const { data } = await api.get("/price");
      setBtcPrice(parseFloat(data.btc_usd).toFixed(2));
    } catch (err) {
      console.error("Failed to fetch BTC price", err);
    }
  };

  const fetchPlan = async () => {
    try {
      const { data } = await api.get("/buyplans", { params: { user_id: userInfo.id } });
      setPlan(data[0] || null);
    } catch (err) {
      console.error("Failed to fetch buy plan", err);
    } finally {
      setPlanLoading(false);
    }
  };

  const fetchTrades = async () => {
    try {
      const { data } = await api.get("/trades", {
        params: { user_id: userInfo.id },
      });

      const sorted = (data || []).sort(
        (a, b) => toUTCDate(b.executed_at) - toUTCDate(a.executed_at)
      );

      setAllTrades(sorted);
      setTrades(sorted.slice(0, 3));
    } catch (err) {
      console.error("Fetch trades failed", err);
    } finally {
      setTradesLoading(false);  // ← add this
    }
  };


  const fetchSmartTimingPerformance = async () => {
    setSmartTimingPerformanceLoaded(false);

    try {
      const { data } = await api.get(
        "/smart-timing/performance"
      );

      setSmartTimingPerformance(
        data || null
      );
    } catch (err) {
      console.error(
        "Fetch Smart Timing performance failed",
        err
      );
      setSmartTimingPerformance(null);
    } finally {
      setSmartTimingPerformanceLoaded(true);
    }
  };

  async function checkStrikeLinked() {
    const access = getAccessToken(ctxToken);
    if (!access) return;

    try {
      const res = await fetch(`${apiBase}/oauth/strike/status`, {
        headers: { Authorization: `Bearer ${access}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      const linked = !!data?.linked;
      try { sessionStorage.setItem("strike_status_cache", JSON.stringify({ linked, auto_refresh: !!data?.auto_refresh })); } catch {}
      setStrikeLinked(linked);
    } catch (e) {
      console.warn("strike status failed", e);
      setStrikeLinked(null);
    }
  }

  // Run on mount / when auth changes
  useEffect(() => { 
    checkStrikeLinked(); 
  }, [jwt, userInfo?.id]);

  // Also run when app returns to foreground or in-app browser closes
  useEffect(() => {
    let resumeSub, browserCloseSub;

    const onReturnFromExternal = async () => {
      // Only respond if the user actually initiated a Strike OAuth or store visit recently
      const oauthTs = Number(localStorage.getItem("strike_oauth_intent_ts") || 0);
      const storeTs = Number(localStorage.getItem("strike_store_click_ts") || 0);
      const hasRecentIntent = 
        (oauthTs && Date.now() - oauthTs < 10 * 60 * 1000) ||
        (storeTs && Date.now() - storeTs < 30 * 60 * 1000);
      if (!hasRecentIntent) return;

      try {
        if (storeTs && Date.now() - storeTs < 30 * 60 * 1000) {
          afTrack("strike_store_returned", { entry: "dashboard" });
          localStorage.removeItem("strike_store_click_ts");
        }
        localStorage.removeItem("strike_oauth_intent_ts");
      } catch {}

      // Show loading while we check
      setStrikeConnecting(true);
      setStrikeRetryPrompt(false);

      // Poll up to 3 times with a short delay — token save can lag slightly
      let linked = false;
      for (let i = 0; i < 3; i++) {
        await new Promise(r => setTimeout(r, i === 0 ? 800 : 1500));
        await checkStrikeLinked();
        const access = getAccessToken(ctxToken);
        try {
          const res = await fetch(`${apiBase}/oauth/strike/status`, {
            headers: { Authorization: `Bearer ${access}` },
            credentials: "include",
          });
          if (res.ok) {
            const data = await res.json();
            if (data?.linked) { linked = true; break; }
          }
        } catch {}
      }

      setStrikeConnecting(false);

      if (linked) {
        setStrikeLinked(true);
        setStrikeRetryPrompt(false);
        setShowStrikeLinkWarning(false);
        setStrikeError(null);

        try {
          sessionStorage.setItem(
            "strike_status_cache",
            JSON.stringify({ linked: true, auto_refresh: true })
          );
        } catch {}

        setPlan((p) =>
          p
            ? {
                ...p,
                strike_auth_revoked_at: null,
                strike_auth_revoked_notified_at: null,
              }
            : p
        );

        try { await fetchPlan(); } catch {}
        try { await fetchSummary(); } catch {}
        try { await fetchTrades(); } catch {}

        afTrack("strike_link_success", { entry: "dashboard_return" });
      } else {
        setStrikeRetryPrompt(true);
      }
    };

    (async () => {
      resumeSub = await App.addListener("resume", onReturnFromExternal);
      browserCloseSub = await Browser.addListener("browserFinished", onReturnFromExternal);
    })();

    return () => {
      resumeSub?.remove && resumeSub.remove();
      browserCloseSub?.remove && browserCloseSub.remove();
    };
  }, [jwt, userInfo?.id]); // keep your deps

  useEffect(() => {
    const probeBuyPower = async () => {
      if (!userInfo?.id || !strikeLinked || pendingFrac <= 0) {
        setLoadingProbe(false);
        return;
      }

      if (
        requiredFiat == null ||
        !Number.isFinite(requiredFiat)
      ) {
        setLoadingProbe(false);
        return;
      }

      try {
        setLoadingProbe(true);

        const { data } = await withAuthRetry(async (retried) => {
          if (!retried) {
            try {
              await refreshAccessToken();
            } catch {}
          }

          const fresh = getAccessToken(ctxToken);

          return api.get("/me/strike/can-buy", {
            headers: {
              Authorization: `Bearer ${fresh}`,
            },
            params: {
              amount: Number(requiredFiat).toFixed(2),
              ...(fiatCurrency
                ? { currency: fiatCurrency }
                : {}),
            },
          });
        });

        const can =
          data?.canBuy === true ||
          data?.can_buy === true;

        setCanBuyNow(can);
        setBuyingPowerReason(data?.reason ?? null);
        setFiatCurrency(data?.currency ?? null);

        setAvailableFiatCurrencies(
          Array.isArray(data?.availableCurrencies)
            ? data.availableCurrencies
            : [],
        );

        const rawBuyable =
          data?.buyable ??
          data?.fiatBuyable ??
          data?.usdBuyable ??
          data?.usd_buyable;

        const parsed = Number(rawBuyable);

        setFiatBuyable(
          rawBuyable != null && Number.isFinite(parsed)
            ? parsed
            : null,
        );

      } catch (error) {
        console.warn("Strike buying-power probe failed", error);
        setCanBuyNow(null);
        setFiatBuyable(null);
        setBuyingPowerReason("probe_failed");

      } finally {
        setLoadingProbe(false);
      }
    };

    probeBuyPower();
  }, [
    ctxToken,
    userInfo?.id,
    strikeLinked,
    requiredFiat,
    fiatCurrency,
    pendingFrac,
    apiBase,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function loadBuyingPower() {
      if (!strikeLinked) return;

      try {
        const access = getAccessToken(ctxToken);
        if (!access) return;

        const preferredCurrency =
          plan?.fiat_currency ||
          fiatCurrency ||
          null;

        const { data } = await api.get(
          "/me/strike/buying-power",
          {
            headers: {
              Authorization: `Bearer ${access}`,
            },
            params: preferredCurrency
              ? { currency: preferredCurrency }
              : {},
          },
        );

        if (cancelled) return;

        const returnedCurrency = data?.currency ?? null;
        const rawBuyingPower =
          data?.buying_power ??
          data?.buying_power_usd;

        const buyingPower = Number(rawBuyingPower);

        setFiatCurrency(returnedCurrency);

        setAvailableFiatCurrencies(
          Array.isArray(data?.availableCurrencies)
            ? data.availableCurrencies
            : [],
        );

        if (data?.requiresCurrencySelection === true) {
          setBuyingPowerReason("funding_currency_required");
        } else if (data?.reason) {
          setBuyingPowerReason(data.reason);
        }

        if (
          rawBuyingPower != null &&
          Number.isFinite(buyingPower)
        ) {
          const rounded = Number(buyingPower.toFixed(2));

          sessionStorage.setItem(
            "strike_display_balance_v2",
            JSON.stringify({
              v: rounded,
              currency: returnedCurrency,
              ts: Date.now(),
            }),
          );

          setDisplayBalance(rounded);
          setFiatAvailable(rounded);

          window.dispatchEvent(
            new Event("strike-balance-updated"),
          );
        } else {
          setDisplayBalance(null);
          setFiatAvailable(null);
        }
      } catch (error) {
        console.warn(
          "Strike buying-power load failed",
          error,
        );
      }
    }

    loadBuyingPower();

    return () => {
      cancelled = true;
    };
  }, [
    ctxToken,
    strikeLinked,
    plan?.fiat_currency,
  ]);

  useEffect(() => {
    if (completed === total && showChecklist) dismissChecklist();
  }, [completed, total]); 

  const refreshBalance = useCallback(async () => {
    try {
      const access = getAccessToken(ctxToken);
      const need = Number(
        requiredFiat ??
        plan?.amount_fiat ??
        plan?.amount_usd ??
        0
      );

      // Refresh displayBalance first — this is the primary signal for needsTopUp.
      // Without this, returning from another app leaves displayBalance stale/null
      // and falls through to the probe, which can briefly show a false warning.
      try {
        const { data: bpData } = await api.get("/me/strike/buying-power", {
          headers: { Authorization: `Bearer ${access}` },
        });
        const returnedCurrency = bpData?.currency ?? fiatCurrency;
        const rawBuyingPower = bpData?.buying_power;
        const bp = Number(rawBuyingPower);

        setFiatCurrency(returnedCurrency ?? null);

        setAvailableFiatCurrencies(
          Array.isArray(bpData?.availableCurrencies)
            ? bpData.availableCurrencies
            : [],
        );
        if (Number.isFinite(bp)) {
          const rounded = Number(bp.toFixed(2));
          try {
            sessionStorage.setItem(
              "strike_display_balance_v2",
              JSON.stringify({
                v: rounded,
                currency: returnedCurrency,
                ts: Date.now(),
              }),
            );
          } catch {}
          setDisplayBalance(rounded);
          window.dispatchEvent(new Event("strike-balance-updated"));
        }
      } catch (e) {
        console.warn("refreshBalance buying-power failed, falling back to probe", e);
      }

      const { data } = await api.get("/me/strike/can-buy", {
        headers: { Authorization: `Bearer ${access}` },
        params: {
          amount: need.toFixed(2),
          ...(fiatCurrency
            ? { currency: fiatCurrency }
            : {}),
        },
      });

      const can = data?.canBuy === true || data?.can_buy === true;
      setCanBuyNow(can);

      setBuyingPowerReason(data?.reason ?? null);
      setFiatCurrency(data?.currency ?? fiatCurrency ?? null);

      setAvailableFiatCurrencies(
        Array.isArray(data?.availableCurrencies)
          ? data.availableCurrencies
          : [],
      );

      const rawBuyable =
        data?.buyable ??
        data?.fiatBuyable ??
        data?.usdBuyable ??
        data?.usd_buyable;

      const parsed = Number(rawBuyable);

      setFiatBuyable(
        rawBuyable != null && Number.isFinite(parsed)
          ? parsed
          : null,
      );
    } catch (e) {
      console.error("refreshBalance probe failed", e);
      setCanBuyNow(null);
      setFiatBuyable(null);
      setBuyingPowerReason("probe_failed");
    }
  }, [
      ctxToken,
      requiredFiat,
      plan?.amount_usd,
      fiatCurrency,
    ]);

  useEffect(() => {
    const onFocus = () => refreshBalance();
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshBalance();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshBalance]);

  const normFreq = (plan?.freq || "").toLowerCase().replace("bi-weekly", "bi-weekly");
  const nextStart = nextWindowStart(normFreq, plan?.window_start);
  const remaining = Number(
    plan?.unspent_fiat ??
    plan?.unspent_fiat_usd ??
    0
  );

  // guard / clamp
  const remainingUsd = Math.max(0, Number.isFinite(remaining) ? remaining : 0);

  const DEV_PLAN_PREVIEW_ENABLED =
    import.meta.env.DEV ||
    import.meta.env.VITE_ENABLE_PLAN_PREVIEW === "true";

  function makePreviewPlan(state) {
    const now = new Date();

    const windowStart = new Date(now);
    windowStart.setDate(windowStart.getDate() - 1);
    windowStart.setHours(20, 0, 0, 0);

    const nextRun = new Date(now);
    nextRun.setDate(nextRun.getDate() + 14);
    nextRun.setHours(20, 0, 0, 0);

    const basePlan = {
      ...(plan || {}),
      id: "preview-plan",
      amount_usd: 10,
      freq: "bi-weekly",
      window_start: windowStart.toISOString(),
      next_run: nextRun.toISOString(),
      strike_auth_revoked_at: null,
    };

    if (state === "smart-complete") {
      return {
        ...basePlan,
        smart_timing: true,
        unspent_fiat_usd: 0,
      };
    }

    if (state === "standard") {
      return {
        ...basePlan,
        smart_timing: false,
        unspent_fiat_usd: 10,
      };
    }

    return {
      ...basePlan,
      smart_timing: true,
      unspent_fiat_usd: 10,
    };
  }

  const urlPlanPreviewState = DEV_PLAN_PREVIEW_ENABLED
    ? new URLSearchParams(window.location.search).get("planPreview")
    : null;

  const planPreviewState = localPlanPreviewState || urlPlanPreviewState;

  const displayPlan = planPreviewState
    ? makePreviewPlan(planPreviewState)
    : plan;

  const latestFinishedSmartBuy = useMemo(() => {
    if (planPreviewState === "smart-complete") {
      return {
        key: "preview-complete-window",
        totalSats: 12513,
        executionLabel: "1 buy",
        avgBuyPrice: 84327.42,
      };
    }

    if (!plan?.id) return null;

    const currentWindowStart = toUTCDate(plan.window_start).getTime();

    const group = allTradeWindows.find((w) => {
      if (
        !w.isSmartTiming ||
        !w.hasRealWindowStart ||
        String(w.planId) !== String(plan.id) ||
        !(Number(w.totalSats) > 0)
      ) {
        return false;
      }

      const groupStart = toUTCDate(w.windowStart).getTime();

      // Once Smart Timing finishes buying, the backend advances
      // plan.window_start to the upcoming window. Therefore the
      // most recent trade window before that anchor is the buy
      // we want to show while waiting.
      return (
        Number.isFinite(groupStart) &&
        (
          !Number.isFinite(currentWindowStart) ||
          groupStart <= currentWindowStart
        )
      );
    });

    if (!group) return null;

    return {
      key: group.key,
      totalSats: Math.round(group.totalSats),
      executionLabel: group.executionLabel,
      avgBuyPrice: Number.isFinite(group.avgBuyPrice)
        ? group.avgBuyPrice
        : null,
    };
  }, [
    planPreviewState,
    plan?.id,
    plan?.window_start,
    allTradeWindows,
  ]);

  function minutesUntil(d) { return (d - new Date()) / 60000; }

  function getWindowState(plan) {
    const now = new Date();
    const wStart = plan?.window_start ? parseApiDateUtc(plan.window_start) : null;
    const wLenDays = windowDays(plan?.freq);
    const wEnd = wStart ? new Date(wStart.getTime() + wLenDays * 24 * 3600 * 1000) : null;

    const nxt = plan?.next_run ? parseApiDateUtc(plan.next_run) : null;
    const nextWin = (wStart && plan?.freq) ? nextWindowStart(plan.freq, plan.window_start) : null;

    // Heuristic thresholds
    const LONG_DELAY_MIN = 60;          // > 60 minutes = not a normal 3–5 min tick
    const isFuture = nxt && nxt > now;
    const longDelay = isFuture && minutesUntil(nxt) > LONG_DELAY_MIN;

    // 1) Between windows: DB advanced window_start into the future.
    if (wStart && now < wStart) {
      return {
        mode: "between",
        label: "Between windows",
        desc: "Your next window hasn’t begun yet.",
        windowStart: wStart,
        nextWindow: nextWin,
        remainingUsd: 0,
      };
    }

    // 2) Paused/pushed back: next_run is far out (e.g., next midnight) and not just “wait for window”.
    //    If next_run is beyond *now* by a long time and (a) it's before the next window
    //    or (b) the window is already active → consider it paused.
    if (longDelay && (!nextWin || nxt < nextWin || (wStart && now >= wStart))) {
      return {
        mode: "paused",
        label: "Paused",
        desc: `Retry on ${fmtDate(nxt)}`,
        windowStart: wStart,
        nextWindow: nextWin,
        remainingUsd: Number(plan?.unspent_fiat_usd ?? 0),
      };
    }

    // 3) Active window
    if (wStart && wEnd && now >= wStart && now < wEnd) {
      return {
        mode: "active",
        label: "Active",
        windowStart: wStart,
        nextWindow: nextWin,
        remainingUsd: Math.max(0, Number(plan?.unspent_fiat_usd ?? 0)),
      };
    }

    // 4) Fallback
    return {
      mode: "unknown",
      label: "Status",
      windowStart: wStart,
      nextWindow: nextWin,
      remainingUsd: Math.max(0, Number(plan?.unspent_fiat_usd ?? 0)),
    };
  }

  /* ──────────────────────────────────────────────────────────
     memo‑ised chart data (unchanged)
  ────────────────────────────────────────────────────────── */
  const chartData = useMemo(() => {
    if (!summary?.dca_data?.length) return null;

    const labels = summary.dca_data.map((t) => new Date(t.executed_at).toLocaleDateString());

    let satsAccum = 0;
    let usdAccum = 0;
    const cumulative = [];
    const avgCost = [];

    summary.dca_data.forEach((t) => {
      const sats = t.btc_amt * 100_000_000;
      satsAccum += sats;
      usdAccum += t.usd_cost;
      cumulative.push(satsAccum);
      avgCost.push(usdAccum / satsAccum);
    });

    return { labels, cumulative, avgCost };
  }, [summary]);

  const referralMonthsEarned =
    referralStats?.months_earned ??
    referralStats?.qualified_referrals ??
    0;

  const hasReferralReward =
    referralMonthsEarned > 0;

  /* ──────────────────────────────────────────────────────────
     UI
  ────────────────────────────────────────────────────────── */
  return (
    <>
      {/* ── Bitcoin Education Sheet ── */}
      {showEduSheet && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={dismissEduSheet}
          />
          {/* Sheet */}
          <div className="relative w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-t-2xl px-6 pt-6 pb-10 shadow-xl">
            {/* Drag handle */}
            <div className="w-10 h-1 bg-zinc-700 rounded-full mx-auto mb-6" />

            {/* Slide content */}
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">{EDU_SLIDES[eduSlide].icon}</div>
              <h2 className="text-lg font-bold text-white mb-2">
                {EDU_SLIDES[eduSlide].title}
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed">
                {EDU_SLIDES[eduSlide].body}
              </p>
            </div>

            {/* Dot indicators */}
            <div className="flex justify-center gap-2 mb-6">
              {EDU_SLIDES.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${i === eduSlide ? "bg-orange-500" : "bg-zinc-700"}`}
                />
              ))}
            </div>

            {/* Actions */}
            {eduSlide < EDU_SLIDES.length - 1 ? (
              <div className="flex gap-3">
                {eduSlide > 0 ? (
                  <button
                    type="button"
                    onClick={() => setEduSlide(s => s - 1)}
                    className="flex-1 py-3 rounded-xl border border-zinc-700 text-zinc-400 text-sm hover:border-zinc-500 transition"
                  >
                    Back
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={dismissEduSheet}
                    className="flex-1 py-3 rounded-xl border border-zinc-700 text-zinc-400 text-sm hover:border-zinc-500 transition"
                  >
                    Skip
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setEduSlide(s => s + 1);
                    afTrack("edu_sheet_next", { slide: eduSlide });
                  }}
                  className="flex-1 py-3 rounded-xl bg-orange-500 text-white font-semibold text-sm hover:bg-orange-600 transition"
                >
                  Next
                </button>
              </div>
            ) : (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setEduSlide(s => s - 1)}
                  className="flex-1 py-3 rounded-xl border border-zinc-700 text-zinc-400 text-sm hover:border-zinc-500 transition"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => {
                    afTrack("edu_sheet_completed", {});
                    dismissEduSheet();
                  }}
                  className="flex-1 py-3 rounded-xl bg-orange-500 text-white font-semibold text-sm hover:bg-orange-600 transition"
                >
                  Got it
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bitcoin Price (always) + Sats Stacked (only if logged in) */}
      {userInfo?.id ? (
        /* ───────── Logged‑IN layout ───────── */
        <div className="flex flex-col gap-6 mb-10 w-full max-w-lg min-w-0 overflow-x-hidden">
          <PriceChart key={userInfo?.id ?? "none"} btcPrice={btcPrice} jwt={jwt} apiBase={apiBase} planFreq={plan?.freq || null} userId={userInfo?.id ?? null} />

          {!planLoading &&
            pendingSmartTimingCheckout &&
            plan?.smart_timing !== true && (
              <div className="ss-card ss-card-pad border border-orange-500/30">
                <p className="text-sm font-bold text-orange-400">
                  Continue Smart Timing setup
                </p>

                <p className="mt-1 text-sm text-textMuted leading-relaxed">
                  {userInfo?.is_premium
                    ? "Your Smart Timing subscription is active, and your saved plan setup is waiting to be finished."
                    : "We saved the Smart Timing plan you were setting up so you don't have to start over."}
                </p>

                <button
                  type="button"
                  onClick={() => {
                    afTrack(
                      "smart_timing_checkout_recovery_clicked",
                      {
                        entry: "dashboard",
                        action:
                          pendingSmartTimingCheckout?.kind ||
                          "unknown",
                      }
                    );

                    navigate(
                      "/plans?smartTiming=1"
                    );
                  }}
                  className="mt-4 rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-semibold px-4 py-2 transition"
                >
                  Continue setup
                </button>
              </div>
            )}

                    {/* Only show Sats Stacked once the user has actually accumulated some */}
          {summary?.total_sats > 0 && (
            <div className="ss-card ss-card-pad w-full">
              <div className="flex items-start justify-between gap-3">

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <p className="ss-section-title">
                      {showBTC ? "BTC STACKED" : "SATS STACKED"}
                    </p>

                    {!hideStack && (
                      <button
                        type="button"
                        onClick={() => setShowBTC((v) => !v)}
                        className="text-[11px] px-2 py-0.5 rounded-full border border-border text-textMuted hover:text-white hover:bg-zinc-800 transition tabular-nums"
                        aria-label={
                          showBTC
                            ? "Show total in sats"
                            : "Show total in BTC"
                        }
                        title={showBTC ? "Show sats" : "Show BTC"}
                      >
                        {showBTC ? "Show sats" : "Show BTC"}
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => setHideStack((v) => !v)}
                      className="text-[11px] px-2 py-0.5 rounded-full border border-border text-textMuted hover:text-white hover:bg-zinc-800 transition"
                      aria-label={
                        hideStack
                          ? "Show stacked balance"
                          : "Hide stacked balance"
                      }
                      title={hideStack ? "Show balance" : "Hide balance"}
                    >
                      {hideStack ? "Show" : "Hide"}
                    </button>
                  </div>

                  <p
                    className={`text-2xl font-bold text-white tabular-nums ${
                      hideStack ? "tracking-widest" : ""
                    }`}
                  >
                    {summary ? (
                      fmtStackValue(
                        summary.total_sats,
                        showBTC,
                        hideStack
                      )
                    ) : (
                      <span className="inline-block w-32 h-6 bg-zinc-700 rounded animate-pulse" />
                    )}
                  </p>
                </div>

                {/* Decorative stacking bars */}
                {!hideStack && (
                  <div
                    className="flex h-8 shrink-0 items-end gap-1 self-center pr-1"
                    aria-hidden="true"
                  >
                    <span className="h-[9px] w-1 rounded-full bg-orange-500" />
                    <span className="h-[13px] w-1 rounded-full bg-orange-500" />
                    <span className="h-[18px] w-1 rounded-full bg-orange-500" />
                    <span className="h-[24px] w-1 rounded-full bg-orange-500" />
                  </div>
                )}

              </div>
            </div>
          )}
          {/* Strike connection error */}
          {strikeError && (
            <div className="mb-2 max-w-lg flex items-start justify-between gap-3 bg-red-950/40 border border-red-800/60 text-red-300 rounded-xl px-4 py-3 text-sm">
              <span>{strikeError}</span>
              <button
                type="button"
                onClick={() => setStrikeError(null)}
                className="shrink-0 text-red-400 hover:text-red-200 text-base leading-none"
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          )}
          {/* ── Subscription expiry warning ────────────────────────────────── */}
          {userInfo?.id && subExpiryWarning && !subExpiryDismissed && (() => {
            const { level, daysLeft, exp } = subExpiryWarning;

            const dayLabel = daysLeft < 1
              ? "less than a day"
              : daysLeft < 2
              ? "1 day"
              : `${Math.floor(daysLeft)} days`;

            const expDateLabel = exp.toLocaleDateString(undefined, {
              month: "short", day: "numeric", year: "numeric",
            });

            const colors = {
              critical: {
                wrap:  "bg-red-950/50 border-red-700/70",
                icon:  "text-red-400",
                title: "text-red-200",
                body:  "text-red-300/90",
                btn:   "bg-red-600 hover:bg-red-500 text-white",
                dismiss: "text-red-500 hover:text-red-300",
              },
              warning: {
                wrap:  "bg-amber-950/50 border-amber-700/60",
                icon:  "text-amber-400",
                title: "text-amber-200",
                body:  "text-amber-300/90",
                btn:   "bg-amber-500 hover:bg-amber-400 text-black",
                dismiss: "text-amber-600 hover:text-amber-300",
              },
              notice: {
                wrap:  "bg-zinc-900/70 border-zinc-700/60",
                icon:  "text-orange-400",
                title: "text-white",
                body:  "text-textMuted",
                btn:   "bg-orange-500 hover:bg-orange-400 text-black",
                dismiss: "text-zinc-500 hover:text-zinc-300",
              },
            }[level];

            const icon = level === "critical" ? AlertTriangle : level === "warning" ? AlertTriangle : Clock;
            const IconComponent = icon;

            return (
              <div className={`mb-4 max-w-lg rounded-xl border px-4 py-3 ${colors.wrap}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <IconComponent className={`mt-0.5 shrink-0 w-4 h-4 ${colors.icon}`} aria-hidden="true" />
                    <div>
                      <p className={`font-semibold text-sm ${colors.title}`}>
                        Smart Timing subscription expires in {dayLabel}
                      </p>
                      <p className={`mt-0.5 text-xs ${colors.body}`}>
                        Your subscription ends on {expDateLabel}. Renew in the{" "}
                        {/iPhone|iPad|iPod/i.test(navigator.userAgent) || Capacitor.getPlatform() === "ios"
                          ? "App Store"
                          : "Play Store"}{" "}
                        to keep Smart Timing running without interruption.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={dismissSubExpiryWarning}
                    className={`shrink-0 text-base leading-none mt-0.5 ${colors.dismiss}`}
                    aria-label="Dismiss"
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ────────────────────────────────────────────────────────────────── */}

          {calcBanner && !plan && (
            <div className="mb-6 max-w-lg bg-card p-5 rounded-xl border border-border shadow-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white mb-1">
                    Your Smart Timing plan is ready
                  </div>
                  <div className="text-xs text-textMuted leading-relaxed">
                    Historically you could stack{" "}
                    <span className="text-orange-400 font-semibold">
                      +{calcBanner.extra_sats?.toLocaleString()} sats
                    </span>
                    {calcBanner.extra_pct != null && (
                      <> ({Number(calcBanner.extra_pct).toFixed(2)}% more)</>
                    )}{" "}
                    vs flat DCA on a ${calcBanner.amount} {calcBanner.freq} plan
                  </div>
                </div>
                <button
                  onClick={() => {
                    setCalcBanner(null);
                    try { localStorage.removeItem("calc_prefill"); } catch {}
                  }}
                  className="text-textMuted hover:text-white transition text-lg leading-none flex-shrink-0"
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
              <button
                onClick={() => navigate("/plans")}
                className="mt-3 w-full py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold transition"
              >
                Create plan
              </button>
            </div>
          )}

          {userInfo?.id &&
            plan?.smart_timing !== true &&
            completed < total &&
            currentSmartTimingHighlight && (
              <div className="mb-4 max-w-lg rounded-xl border border-orange-500/15 bg-orange-500/5 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-white">
                      What Smart Timing is finding
                    </p>

                    <p className="mt-0.5 text-[11px] text-textMuted">
                      Anonymous recent completed window from another SatStacker user
                    </p>
                  </div>

                  <span className="shrink-0 text-[10px] text-zinc-500">
                    {formatHighlightDate(
                      currentSmartTimingHighlight.completed_at
                    )}
                  </span>
                </div>

                <div className="mt-3 flex items-center gap-2 text-sm">
                  <span className="text-zinc-500 tabular-nums">
                    {formatHighlightSats(
                      currentSmartTimingHighlight.scheduled_sats
                    )} sats
                  </span>

                  <span className="text-zinc-600">→</span>

                  <span className="font-semibold text-white tabular-nums">
                    {formatHighlightSats(
                      currentSmartTimingHighlight.smart_sats
                    )} sats
                  </span>
                </div>

                <div className="mt-1 text-lg font-bold text-orange-400">
                  +{Number(
                    currentSmartTimingHighlight.more_btc_pct
                  ).toFixed(2)}% more BTC
                </div>

                <p className="mt-0.5 text-[11px] text-textMuted">
                  vs scheduled DCA
                </p>
              </div>
            )}
          
          {/* Onboarding checklist (gamified) */}
          {userInfo?.id && showChecklist && completed < total && (
            <div className="mb-6 max-w-lg bg-card p-5 rounded-xl border border-border shadow-card">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold">Getting started</p>
                <button
                  type="button"
                  onClick={dismissChecklist}
                  className="text-xs text-textMuted hover:underline"
                  aria-label="Dismiss"
                  title="Dismiss"
                >
                  Dismiss
                </button>
              </div>

              {/* Progress bar */}
              <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden mb-4">
                <div
                  className="h-full bg-orange-500"
                  style={{ width: `${pct}%`, transition: "width 300ms ease" }}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={pct}
                />
              </div>
              <p className="text-xs text-textMuted mb-4">{completed}/{total} steps complete</p>

              <ul className="space-y-2">
                {steps.map((s) => (
                  <li
                    key={s.id}
                    className="flex flex-col gap-2 bg-zinc-800/40 border border-border rounded-lg px-3 py-2"
                  >
                    {/* top row */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] ${
                            s.done ? "bg-green-500 text-black" : "bg-zinc-700 text-textMuted"
                          }`}
                          aria-hidden="true"
                        >
                          {s.done ? "✓" : "•"}
                        </span>
                        <span className={`text-sm ${s.done ? "text-white" : "text-textMuted"}`}>
                          {s.label}
                        </span>
                      </div>

                      {!s.done && (
                        <button
                          type="button"
                          onClick={() => {
                            afTrack("onboarding_cta_tapped", {
                              entry: "dashboard",
                              step_id: s.id,           // plan | strike | firstbuy
                              step_label: s.label,
                              completed,
                              total,
                            });
                            s.action();
                          }}
                          className="text-xs bg-orange-500 text-white px-3 py-1.5 rounded-md hover:bg-orange-600"
                        >
                          {s.actionLabel}
                        </button>
                      )}
                    </div>

                    {/* subtext just for the Strike step */}
                    {s.id === "strike" && !s.done && (
                      <div className="text-xs text-textMuted mt-1 leading-relaxed">
                        <p>
                          SatStacker connects to Strike to execute your Bitcoin buys using
                          your Strike balance. SatStacker never holds your cash or Bitcoin.
                        </p>

                        <p className="mt-1">
                          New to Strike? Download the app and create a free account first.{" "}
                          <button
                            type="button"
                            onClick={openStrikeStore}
                            className="underline"
                          >
                            {Capacitor.isNativePlatform()
                              ? Capacitor.getPlatform() === "android"
                                ? "Get it on Android"
                                : "Get it on iOS"
                              : "Get it on iOS"}
                          </button>
                        </p>
                      </div>
                    )}

                    {s.id === "strike" && !s.done && strikeConnecting && (
                      <div className="flex items-center gap-2 text-xs text-zinc-400 mt-1">
                        <span className="animate-spin">⏳</span> Connecting Strike...
                      </div>
                    )}

                    {s.id === "strike" && !s.done && strikeRetryPrompt && (
                      <div className="mt-2 p-2 rounded-lg bg-zinc-900 border border-zinc-700 text-xs text-zinc-300">
                        Strike wasn't connected — the browser may have closed early.{" "}
                        <button
                          onClick={() => { setStrikeRetryPrompt(false); handleConnectStrike(); }}
                          className="text-orange-400 hover:text-orange-300 underline font-medium transition"
                        >
                          Try again
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>

              {/* Smart next step nudge */}
              <div className="mt-3 text-[12px] text-textMuted">
                { !stepPlanDone ? "Tip: Start by setting your buy amount & frequency." :
                  !stepStrikeDone ? "Your plan is ready. Connect Strike to activate it." :
                  "Tip: Add funds in Strike so your first buy can run." }
              </div>
            </div>
          )}
          {userInfo?.id && planLoading && (
            <div className="mb-6 max-w-lg ss-card ss-card-pad animate-pulse">
              <div className="h-4 w-40 bg-zinc-700 rounded mb-3" />
              <div className="h-3 w-64 bg-zinc-800 rounded" />
            </div>
          )}
          {/* No plan yet banner */}
          {(!plan && !planLoading && (!showChecklist || completed === total)) && (
            <div className="mb-6 max-w-lg ss-card ss-card-pad bg-zinc-950/40">
              <p className="font-semibold text-white">You haven’t created a Buy Plan yet</p>
              <p className="text-sm text-textMuted mt-1">
                Start with as little as $10/week. Smart Timing looks for better
                Bitcoin entry prices inside each buy window.
              </p>
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => navigate("/plans")}
                  className="mt-3 rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-semibold px-4 py-2 transition"
                >
                  Create a Buy Plan
                </button>
              </div>
            </div>
          )}
          {userInfo?.id && !plan && !planLoading && (!showChecklist || completed === total) && (
            <div className="mb-6 max-w-lg ss-card ss-card-pad">
              <p className="text-sm font-semibold text-white mb-2">How this works</p>
              <ul className="space-y-1 text-sm text-zinc-300">
                <li className="flex gap-2">
                  <span className="text-emerald-400">✓</span>
                  <span>Creating a Buy Plan is free. There is no subscription required.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-emerald-400">✓</span>
                  <span>Bitcoin purchases execute through Strike using your Strike balance when the plan runs.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-emerald-400">✓</span>
                  <span>SatStacker never holds your BTC, and you can revoke Strike access anytime.</span>
                </li>
              </ul>
            </div>
          )}
          {userInfo?.id && strikeLinked === false && !planLoading && (!showChecklist || completed === total) && (
            <div className="mb-6 max-w-lg ss-card ss-card-pad bg-zinc-950/40">
              <p className="mb-2 font-semibold">{plan ? "Connect Strike to activate your plan" : "Connect Strike to enable buys"}</p>
              <p className="text-xs text-textMuted mb-2 leading-relaxed">
                SatStacker uses your Strike balance to execute your scheduled Bitcoin buys.
                SatStacker never holds your cash or Bitcoin.
              </p>

              <p className="text-xs text-textMuted mb-2">
                New to Strike? Download the app and create a free account first.
              </p>
              <button
                type="button"
                onClick={openStrikeStore}
                className="text-xs text-textMuted underline mb-3"
              >
                {Capacitor.isNativePlatform()
                  ? (Capacitor.getPlatform() === 'android' ? "Get it on Android →" : "Get it on iOS →")
                  : "Get it on iOS →"}
              </button>
              <div>
                <button
                  type="button"
                  disabled={strikeConnecting}
                  onClick={() => {
                    setStrikeRetryPrompt(false);
                    setShowStrikeLinkWarning(true);
                  }}
                  className="rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-semibold px-4 py-2 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {strikeConnecting ? "Connecting..." : "Reconnect Strike"}
                </button>
              </div>

              {strikeConnecting && (
                <div className="flex items-center gap-2 text-sm text-zinc-400 mt-2">
                  <span className="animate-spin">⏳</span> Connecting Strike...
                </div>
              )}

              {strikeRetryPrompt && !strikeLinked && (
                <div className="mt-3 p-3 rounded-xl bg-zinc-900 border border-zinc-700 text-sm text-zinc-300">
                  No Strike account yet? Download Strike, create an account, then tap Connect. Already have one?{" "}
                  <button
                    onClick={() => { setStrikeRetryPrompt(false); handleConnectStrike(); }}
                    className="text-orange-400 hover:text-orange-300 underline font-medium transition"
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Strike auth revoked banner (highest priority, blocks all buys) */}
          {userInfo?.id && plan?.strike_auth_revoked_at && (
            <div className="mb-6 max-w-lg ss-card ss-card-pad bg-red-950/40 border border-red-700/50">
              <p className="mb-1 font-semibold text-red-200">Reconnect Strike to keep stacking</p>
              <p className="text-sm text-red-100/90">
                We tried to buy the dip but your Strike connection has expired or lost permissions.
                Your plan is paused until you reconnect.
              </p>
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  disabled={strikeConnecting}
                  onClick={() => {
                    setStrikeRetryPrompt(false);
                    setShowStrikeLinkWarning(true);
                  }}
                  className="inline-block bg-black text-white px-3 py-1.5 rounded-md hover:bg-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {strikeConnecting ? "Connecting..." : "Reconnect Strike"}
                </button>
              </div>
            </div>
          )}
          {/* Funding banner (reuses the same needsTopUp logic) */}
          {userInfo?.id &&
            strikeLinked &&
            plan &&
            !plan.strike_auth_revoked_at &&         // NEW
            requiredFiat != null &&
            decisionReady &&
            needsTopUp &&
            typeof displayBalance === "number" && (
            <div className="mb-6 max-w-lg ss-card ss-card-pad bg-zinc-950/40">
              <p className="mb-1 font-semibold">Add funds in Strike to run your next buy</p>
              <p className="text-sm">
                Buying Power: ${displayBalance.toFixed(2)} · Needed: ${Number.isFinite(neededFiat) ? Number(neededFiat).toFixed(2) : "--"}
              </p>
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={openStrike}
                  className="inline-block bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white px-3 py-1.5 rounded-md font-semibold transition"
                >
                  Open Strike
                </button>
                <button
                  onClick={refreshBalance}
                  className="border border-zinc-700 bg-zinc-900 px-3 py-1.5 rounded-md text-zinc-300 hover:bg-zinc-800 hover:text-white transition"
                >
                  Refresh
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Logged-in content */}
      {userInfo?.id ? (
        <>
          {DEV_PLAN_PREVIEW_ENABLED && (
            <div className="mb-3 max-w-lg flex gap-2 text-xs">
              <button
                type="button"
                onClick={() => setLocalPlanPreviewState("smart-active")}
                className="px-3 py-1.5 rounded-lg border border-border text-textMuted"
              >
                Smart active
              </button>

              <button
                type="button"
                onClick={() => setLocalPlanPreviewState("smart-complete")}
                className="px-3 py-1.5 rounded-lg border border-border text-textMuted"
              >
                Smart complete
              </button>

              <button
                type="button"
                onClick={() => setLocalPlanPreviewState("standard")}
                className="px-3 py-1.5 rounded-lg border border-border text-textMuted"
              >
                Standard
              </button>
            </div>
          )}
          {displayPlan && !planLoading && (
            <HuntingWindowCard
              plan={displayPlan}
              completedWindow={latestFinishedSmartBuy}
              onManagePlan={() => navigate("/plans")}
              smartTimingNudgeDismissed={smartTimingNudgeDismissed}
              onDismissSmartTimingNudge={dismissSmartTimingNudge}
            />
          )}

          {shouldShowSmartTimingUpsell && (
            <div className="mb-6 max-w-lg rounded-2xl border border-orange-500/25 bg-orange-500/[0.06] p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-white">
                      Your plan is using Standard timing
                    </span>

                    <span className="rounded-full border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[10px] font-bold text-zinc-400">
                      Free
                    </span>
                  </div>

                  <p className="mt-2 text-sm leading-relaxed text-zinc-300">
                    Your last buy ran at the scheduled time.
                    Smart Timing watches the full buy window and looks for a better
                    Bitcoin price before buying.
                  </p>

                  <p className="mt-2 text-xs text-zinc-400">
                    <span className="font-semibold text-white">
                      1 month free
                    </span>
                    {" · "}
                    then $1.99/month
                    {" · "}
                    cancel anytime
                  </p>

                  <button
                    type="button"
                    onClick={() => {
                      afTrack("smart_timing_dashboard_upsell_clicked", {
                        entry: "post_standard_buy",
                        plan_id: plan?.id ?? null,
                        completed_buys: allTrades.length,
                      });

                      navigate("/plans?smartTiming=true");
                    }}
                    className="mt-4 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-orange-600 active:bg-orange-700"
                  >
                    Try Smart Timing free
                  </button>
                </div>

                <button
                  type="button"
                  aria-label="Dismiss Smart Timing offer"
                  onClick={(e) => {
                    afTrack("smart_timing_dashboard_upsell_dismissed", {
                      entry: "post_standard_buy",
                      plan_id: plan?.id ?? null,
                    });

                    dismissSmartTimingNudge(e);
                  }}
                  className="shrink-0 px-1 text-lg leading-none text-zinc-500 hover:text-zinc-300"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {userInfo?.id &&
            smartTimingPerformance?.benchmark === "scheduled_window_end" &&
            smartTimingSummary?.has_shareable_result === true &&
            Number(smartTimingSummary?.completed_windows || 0) > 0 &&
            Number(smartTimingSummary?.gain_pct) > 0 &&
            Number(smartTimingSummary?.delta_sats) > 0 && (
            <div className="mb-6 max-w-lg ss-card ss-card-pad">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">
                    Your Smart Timing
                  </p>
                  <p className="text-xs text-textMuted mt-1 leading-relaxed">
                    Across completed windows vs scheduled DCA.
                  </p>
                </div>

                <div className="shrink-0 text-right">
                  <div
                    className={`text-2xl font-black tabular-nums leading-none ${
                      Number(smartTimingSummary.gain_pct) >= 0
                        ? "text-orange-400"
                        : "text-zinc-200"
                    }`}
                  >
                    {formatSignedPct(
                      smartTimingSummary.gain_pct
                    )}
                  </div>
                  <div className="text-[11px] text-textMuted mt-1">
                    vs scheduled DCA
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-border bg-zinc-950/40 px-4 py-3">
                <p
                  className={`text-xl font-black tabular-nums ${
                    Number(smartTimingSummary.delta_sats) >= 0
                      ? "text-orange-400"
                      : "text-zinc-200"
                  }`}
                >
                  {formatSignedSats(
                    smartTimingSummary.delta_sats
                  )} sats
                </p>

                <p className="text-xs text-textMuted mt-1">
                  Difference vs scheduled DCA across{" "}
                  {Number(
                    smartTimingSummary.completed_windows || 0
                  ).toLocaleString()} completed{" "}
                  {Number(
                    smartTimingSummary.completed_windows || 0
                  ) === 1
                    ? "window"
                    : "windows"}. Open windows are excluded.
                </p>
                {unverifiedCompletedWindowCount > 0 && (
                  <p className="text-xs text-textMuted mt-1">
                    {unverifiedCompletedWindowCount} earlier completed {unverifiedCompletedWindowCount === 1 ? "window is" : "windows are"}
                    {" "}could not be independently checked in this view. They are still included in the lifetime completed-window result.
                  </p>
                )}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg border border-border bg-zinc-950/40 px-3 py-2">
                  <div className="text-textMuted">Best window</div>
                  <div className="mt-0.5 text-white font-semibold tabular-nums">
                    {smartTimingSummary.best_window_pct != null
                      ? formatSignedPct(
                          smartTimingSummary.best_window_pct
                        )
                      : "—"}
                  </div>
                </div>

                <div className="rounded-lg border border-border bg-zinc-950/40 px-3 py-2">
                  <div className="text-textMuted">Windows ahead</div>
                  <div className="mt-0.5 text-white font-semibold tabular-nums">
                    {Number(
                      smartTimingSummary.positive_windows || 0
                    ).toLocaleString()}
                    {" / "}
                    {Number(
                      smartTimingSummary.completed_windows || 0
                    ).toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2">
                {smartTimingSummary.has_shareable_result && (
                  <button
                    type="button"
                    onClick={shareSmartTimingImpact}
                    className="w-full rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-border text-white py-2.5 font-semibold transition"
                  >
                    Share my Smart Timing result
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    afTrack("referral_cta_tapped", {
                      entry: "dashboard_smart_timing_card",
                    });

                    navigate("/share");
                  }}
                  className="w-full rounded-xl bg-orange-500 hover:bg-orange-600 text-white py-2.5 font-black transition"
                >
                  {hasReferralReward
                    ? "Give another friend 2 months free →"
                    : "Give a friend 2 months free →"}
                </button>

                <p className="text-[11px] text-textMuted text-center leading-relaxed">
                  {hasReferralReward
                    ? `You've earned ${referralMonthsEarned} free ${
                        referralMonthsEarned === 1 ? "month" : "months"
                      } so far.`
                    : "They get an extra free month. When they start Smart Timing, you earn a free month too."}
                </p>
              </div>
            </div>
          )}

          {plan?.smart_timing === true &&
            smartTimingPerformanceLoaded &&
            !tradesLoading &&
            !activeUnfinishedWindow &&
            unverifiedCompletedWindowCount > 0 &&
            !smartTimingSummary && (
              <div className="mb-6 max-w-lg rounded-2xl border border-border bg-zinc-900/40 p-4">
                <p className="text-sm font-semibold text-white">Earlier completed-window results unavailable</p>
                <p className="mt-1 text-xs text-textMuted leading-relaxed">
                  {unverifiedCompletedWindowCount} completed {unverifiedCompletedWindowCount === 1 ? "window still needs" : "windows still need"}
                  {" "}historical verification. An open window never counts as a failed comparison.
                  Your purchases remain available in Trade History.
                </p>
              </div>
          )}

          {plan?.smart_timing === true &&
            smartTimingPerformanceLoaded &&
            smartTimingPerformance?.summary &&
            !activeUnfinishedWindow &&
            Number(smartTimingPerformance.summary.completed_windows || 0) === 0 && (
            <div className="mb-6 max-w-lg rounded-2xl border border-orange-500/20 bg-orange-500/[0.04] p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-500/10 text-lg">
                  ⚡
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-white">
                    Smart Timing performance is waiting on a completed window
                  </p>

                  <p className="mt-1 text-xs leading-relaxed text-textMuted">
                    Your result will finalize once a buy window closes and the scheduled DCA comparison price is available.
                  </p>

                  <button
                    type="button"
                    onClick={() => {
                      afTrack("referral_cta_tapped", {
                        entry: "dashboard_referral_card",
                        has_smart_timing_impact: false,
                      });

                      navigate("/share");
                    }}
                    className="mt-3 w-full rounded-xl border border-orange-500/30 bg-orange-500/10 py-2.5 text-sm font-bold text-orange-400 transition hover:bg-orange-500/15"
                  >
                    {hasReferralReward
                      ? "Give another friend 2 months free →"
                      : "Give a friend 2 months free →"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Recent trades */}
          <div className="mb-12 max-w-xl">
            <h3 className="text-2xl font-bold mb-4">Recent Trades</h3>
            {tradesLoading ? (
              // skeleton while fetching
              <div className="border border-border rounded-xl overflow-hidden animate-pulse">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex justify-between p-3 border-t border-border first:border-0">
                    <div className="h-3 w-24 bg-zinc-700 rounded" />
                    <div className="h-3 w-16 bg-zinc-700 rounded" />
                    <div className="h-3 w-12 bg-zinc-700 rounded" />
                  </div>
                ))}
              </div>
            ) : recentTradeWindows.length ? (
              <div
                role="button"
                tabIndex={0}
                onClick={() => navigate("/trades")}
                onKeyDown={(e) => e.key === "Enter" && navigate("/trades")}
                className="border border-border rounded-xl overflow-hidden cursor-pointer transition hover:bg-zinc-800/30 focus:outline-none focus:ring-2 focus:ring-orange-500/60"
                title="Open Trade History"
              >
                <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-3 text-xs text-textMuted bg-zinc-800/50">
                  <span>Window</span>
                  <span className="text-right">Sats</span>
                  <span className="text-right">USD</span>
                </div>

                {recentTradeWindows.map((w) => {
                  const key = normalizedWindowKey(w?.planId, w?.windowStart);
                  const result = smartTimingWindowResults.get(key);
                  const comparison = verifiedWindowComparisons.get(key);

                  const isSmartWindow =
                    Array.isArray(w?.trades) &&
                    w.trades.some(
                      (t) => t?.smart_timing === true
                    );

                  return (
                    <div
                      key={w.key}
                      className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-3 border-t border-border text-sm even:bg-zinc-800/30"
                    >
                      <div className="min-w-0">
                        <div className="text-white font-semibold truncate">
                          {w.label}
                        </div>
                        <div className="text-xs text-textMuted">
                          {w.executionLabel}
                          {comparison
                            ? ` · ${formatSignedPct(comparison.gainPct)} vs scheduled DCA`
                            : isSmartWindow
                              ? result
                                ? " · comparison unavailable"
                                : !smartTimingPerformanceLoaded
                                  ? " · comparison loading"
                                  : w.windowEnd && Number.isFinite(toUTCDate(w.windowEnd).getTime()) &&
                                      Date.now() >= toUTCDate(w.windowEnd).getTime()
                                    ? " · comparison unavailable"
                                    : w.windowEnd && Number.isFinite(toUTCDate(w.windowEnd).getTime()) &&
                                        Date.now() < toUTCDate(w.windowEnd).getTime()
                                      ? " · window still open"
                                      : " · comparison pending"
                              : ""}
                        </div>
                      </div>

                      <div className="text-right text-white tabular-nums">
                        {fmtSats0(w.totalSats)}
                      </div>

                      <div className="text-right text-white tabular-nums">
                        {fmtUsd2(w.totalUsd)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-textMuted">No trades yet — your first buy will show up here once your plan runs.</p>
            )}
          </div>

          {userInfo?.id &&
            plan?.smart_timing !== true &&
            smartTimingHighlights.length > 0 &&
            (() => {
            const h =
              smartTimingHighlights[
                smartTimingHighlightIndex %
                  smartTimingHighlights.length
              ];

            const changeHighlight = (direction) => {
              setSmartTimingHighlightIndex((current) => {
                const count = smartTimingHighlights.length;

                if (!count) return 0;

                return (
                  (current + direction + count) %
                  count
                );
              });

              afTrack("smart_timing_highlight_navigated", {
                direction:
                  direction > 0 ? "next" : "previous",
              });
            };

            return (
              <div className="mb-6 max-w-lg ss-card ss-card-pad">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white">
                      Smart Timing Highlights
                    </p>

                    <p className="text-xs text-textMuted mt-1 leading-relaxed">
                      Recent anonymous completed Smart Timing windows across SatStacker.
                    </p>
                  </div>

                  {smartTimingHighlights.length > 1 && (
                    <div className="shrink-0 text-[10px] text-textMuted tabular-nums">
                      {smartTimingHighlightIndex + 1}
                      {" / "}
                      {smartTimingHighlights.length}
                    </div>
                  )}
                </div>

                <div className="mt-4 rounded-xl border border-border bg-zinc-950/40 px-4 py-4">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-xs text-textMuted">
                      Scheduled DCA
                    </span>

                    <span className="text-sm font-semibold text-zinc-300 tabular-nums">
                      {formatHighlightSats(
                        h.scheduled_sats
                      )} sats
                    </span>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-4">
                    <span className="text-xs text-textMuted">
                      Smart Timing
                    </span>

                    <span className="text-sm font-semibold text-white tabular-nums">
                      {formatHighlightSats(
                        h.smart_sats
                      )} sats
                    </span>
                  </div>

                  <div className="mt-4 border-t border-border pt-4 text-center">
                    <div className="text-2xl font-black text-orange-400 tabular-nums">
                      +{Number(
                        h.more_btc_pct
                      ).toFixed(2)}% more BTC
                    </div>

                    <p className="mt-1 text-xs text-textMuted">
                      vs scheduled DCA · +{formatHighlightSats(
                        h.extra_sats
                      )} sats
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <p className="text-[11px] text-textMuted">
                    Completed window ·{" "}
                    {formatHighlightDate(
                      h.completed_at
                    )}
                  </p>

                  {smartTimingHighlights.length > 1 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => changeHighlight(-1)}
                        className="h-8 w-8 rounded-lg border border-border bg-zinc-950/40 text-zinc-400 hover:text-white hover:bg-zinc-800 transition"
                        aria-label="Previous Smart Timing highlight"
                      >
                        ‹
                      </button>

                      <button
                        type="button"
                        onClick={() => changeHighlight(1)}
                        className="h-8 w-8 rounded-lg border border-border bg-zinc-950/40 text-zinc-400 hover:text-white hover:bg-zinc-800 transition"
                        aria-label="Next Smart Timing highlight"
                      >
                        ›
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Charts */}
          {chartData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <ChartBox
                title="Cumulative Sats"
                labels={chartData.labels}
                data={chartData.cumulative}
                color="rgba(247,147,26,0.9)"
              />
              <ChartBox
                title="Avg Cost / Sat"
                labels={chartData.labels}
                data={chartData.avgCost}
                color="rgba(102,255,102,0.9)"
                yFmt={(v) => `$${Number(v).toFixed(6)}`}
              />
            </div>
          )}
        </>
      ) : (
        <>
          {/* Logged-out conversion experience */}
          <div className="w-full px-4">
            <div className="max-w-xl mx-auto flex flex-col gap-6">

              {/* HERO */}
              <section className="bg-card p-7 sm:p-8 rounded-2xl border border-border shadow-card text-center">
                <div className="inline-flex items-center gap-2.5 rounded-full border border-orange-500/20 bg-orange-500/5 px-3.5 py-1.5 mb-5">
                  <span className="h-1.5 w-1.5 rounded-full bg-orange-400" />
                  <span className="text-orange-400 text-xs font-bold tracking-wide">
                    SMART TIMING
                  </span>
                  <span className="h-3 w-px bg-zinc-700" />
                  <span className="text-zinc-400 text-xs font-medium">
                    1 month free
                  </span>
                </div>

                <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight">
                  Automate your Bitcoin DCA.
                  <span className="block text-orange-400">
                    Get smarter entries.
                  </span>
                </h1>

                <p className="mt-4 text-base text-zinc-300 leading-relaxed max-w-md mx-auto">
                  SatStacker connects to Strike and looks for better Bitcoin
                  prices inside each buy window without changing your budget
                  or cadence.
                </p>

                <button
                  type="button"
                  onClick={() => startSmartTimingSignup("dashboard_hero")}
                  className="mt-6 w-full py-3.5 rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-black text-base shadow-lg shadow-orange-500/15 transition"
                >
                  Start Smart Timing free →
                </button>

                <p className="mt-2 text-xs text-zinc-400">
                  $0 today · 1 month free · then $1.99/month · cancel anytime
                </p>

                <button
                  type="button"
                  onClick={() => {
                    afTrack("calculator_cta_tapped", {
                      entry: "dashboard_hero",
                      variant: "calculator_secondary",
                      platform: Capacitor.isNativePlatform()
                        ? Capacitor.getPlatform()
                        : "web",
                      source_screen: "first_open_dashboard",
                    });

                    navigate("/calculator");
                  }}
                  className="mt-4 text-sm font-semibold text-zinc-400 hover:text-white transition"
                >
                  See a Smart Timing example →
                </button>

                <div className="grid grid-cols-3 gap-2 mt-6">
                  <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 px-2 py-4 text-center">
                    <Gauge className="mx-auto h-6 w-6 text-orange-400" strokeWidth={1.8} />
                    <p className="mt-3 text-xs font-bold text-zinc-200 leading-snug">
                      Smarter<br />timing
                    </p>
                  </div>

                  <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 px-2 py-4 text-center">
                    <WalletCards className="mx-auto h-6 w-6 text-zinc-300" strokeWidth={1.8} />
                    <p className="mt-3 text-xs font-bold text-zinc-200 leading-snug">
                      Same budget
                    </p>
                  </div>

                  <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 px-2 py-4 text-center">
                    <ShieldCheck className="mx-auto h-6 w-6 text-zinc-300" strokeWidth={1.8} />
                    <p className="mt-3 text-xs font-bold text-zinc-200 leading-snug">
                      Non-custodial
                    </p>
                  </div>
                </div>

                <p className="mt-5 text-xs text-textMuted">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-zinc-300 hover:text-white underline font-medium"
                  >
                    Log in
                  </Link>
                </p>
              </section>

              {/* HOW IT WORKS */}
              <section className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl shadow-card">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-orange-400">
                  How it works
                </p>

                <h2 className="mt-2 text-xl font-bold text-white">
                  Your DCA. Just smarter.
                </h2>

                <div className="mt-5 space-y-4">
                  {[
                    {
                      n: "1",
                      title: "Choose your amount and cadence",
                      body: "Daily, weekly, or bi-weekly — you stay in control of your regular Bitcoin budget.",
                    },
                    {
                      n: "2",
                      title: "Smart Timing watches your buy window",
                      body: "It looks for better entry prices inside each buy window while keeping your regular schedule and budget.",
                    },
                    {
                      n: "3",
                      title: "Your buys execute through Strike",
                      body: "SatStacker never holds your cash or Bitcoin.",
                    },
                  ].map((step) => (
                    <div
                      key={step.n}
                      className="flex items-start gap-3"
                    >
                      <div className="h-8 w-8 shrink-0 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 flex items-center justify-center font-black text-sm">
                        {step.n}
                      </div>

                      <div>
                        <p className="text-sm font-bold text-white">
                          {step.title}
                        </p>
                        <p className="mt-1 text-xs text-zinc-400 leading-relaxed">
                          {step.body}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* REAL PROOF */}
              {currentSmartTimingHighlight && (
                <section className="rounded-2xl border border-orange-500/20 bg-zinc-950/70 p-6 shadow-card">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.12em] text-orange-400">
                        Recent Smart Timing result
                      </p>

                      <p className="mt-1 text-xs text-textMuted">
                        Anonymous completed SatStacker buy window
                      </p>
                    </div>

                    <span className="text-[11px] text-zinc-500">
                      {formatHighlightDate(
                        currentSmartTimingHighlight.completed_at
                      )}
                    </span>
                  </div>

                  <div className="mt-5 text-center">
                    <div className="text-4xl font-black text-orange-400 tabular-nums">
                      +{Number(
                        currentSmartTimingHighlight.more_btc_pct
                      ).toFixed(2)}%
                    </div>

                    <p className="mt-1 text-sm font-semibold text-white">
                      more Bitcoin vs scheduled DCA
                    </p>

                    <p className="mt-1 text-xs text-zinc-500">
                      +{formatHighlightSats(
                        currentSmartTimingHighlight.extra_sats
                      )} sats in this completed window
                    </p>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 px-3 py-3 text-center">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                        Scheduled DCA
                      </div>
                      <div className="mt-1 text-sm font-semibold text-zinc-300 tabular-nums">
                        {formatHighlightSats(
                          currentSmartTimingHighlight.scheduled_sats
                        )} sats
                      </div>
                    </div>

                    <div className="rounded-xl border border-orange-500/20 bg-orange-500/5 px-3 py-3 text-center">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                        Smart Timing
                      </div>
                      <div className="mt-1 text-sm font-semibold text-orange-400 tabular-nums">
                        {formatHighlightSats(
                          currentSmartTimingHighlight.smart_sats
                        )} sats
                      </div>
                    </div>
                  </div>

                  <p className="mt-3 text-[10px] text-zinc-600 text-center">
                    Individual results vary. Historical result, not a guarantee.
                  </p>
                </section>
              )}

              {/* VISUAL EXPLAINER */}
              <section className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl shadow-card">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">
                  See the difference
                </p>

                <h2 className="mt-2 text-xl font-bold text-white">
                  Give each DCA buy a window to work.
                </h2>

                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                  Smart Timing gets a window to look for a better entry while
                  keeping your regular DCA budget and cadence the same.
                </p>

                <div className="mt-5 flex items-center gap-5 text-xs text-zinc-400">
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-zinc-500" />
                    Scheduled DCA
                  </span>

                  <span className="inline-flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                    Smart Timing
                  </span>
                </div>

                <div className="mt-4 rounded-2xl border border-zinc-800 bg-zinc-950/50 p-3.5">
                  <SmartTimingPreviewSvg />
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-2 py-3">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-600">
                      Budget
                    </div>
                    <div className="mt-1 text-xs font-bold text-white">
                      Same
                    </div>
                  </div>

                  <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-2 py-3">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-600">
                      Cadence
                    </div>
                    <div className="mt-1 text-xs font-bold text-white">
                      Same
                    </div>
                  </div>

                  <div className="rounded-xl border border-orange-500/20 bg-orange-500/5 px-2 py-3">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-600">
                      Timing
                    </div>
                    <div className="mt-1 text-xs font-bold text-orange-400">
                      Smarter
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    afTrack("calculator_cta_tapped", {
                      entry: "dashboard_explainer",
                    });
                    navigate("/calculator");
                  }}
                  className="mt-5 w-full py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white font-semibold transition"
                >
                  Try it with your numbers →
                </button>

                <p className="mt-2 text-[10px] text-textMuted/80 text-center">
                  Historical simulations and results vary. Not investment advice.
                </p>
              </section>

              {/* TRUST */}
              <section className="rounded-2xl border border-zinc-800 bg-card p-6">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">
                  Built for Bitcoiners
                </p>

                <h2 className="mt-2 text-xl font-bold text-white">
                  Your Bitcoin stays yours.
                </h2>

                <div className="mt-4 space-y-3 text-sm text-zinc-300">
                  <div className="flex gap-3">
                    <span className="text-orange-400">✓</span>
                    <span>
                      Buys execute through your connected Strike account.
                    </span>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-orange-400">✓</span>
                    <span>
                      SatStacker never holds your cash or Bitcoin.
                    </span>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-orange-400">✓</span>
                    <span>
                      Pause or change your buy plan anytime.
                    </span>
                  </div>
                </div>
              </section>

              {/* FINAL CTA */}
              <section className="rounded-2xl border border-orange-500/25 bg-orange-500/[0.04] p-7 text-center shadow-card">
                <p className="text-2xl font-black text-white">
                  Put your next DCA window to work.
                </p>

                <p className="mt-2 text-sm text-zinc-400">
                  Try Smart Timing for a month before paying anything.
                </p>

                <button
                  type="button"
                  onClick={() => startSmartTimingSignup("dashboard_bottom_cta")}
                  className="mt-5 w-full py-3.5 rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-black shadow-lg shadow-orange-500/15 transition"
                >
                  Start Smart Timing free →
                </button>

                <p className="mt-2 text-xs text-zinc-400">
                  $0 today · 1 month free · then $1.99/month · cancel anytime
                </p>
              </section>

              <div className="pb-10 text-center text-xs text-textMuted">
                <a
                  href="https://www.satstacker.app/privacy"
                  className="hover:underline"
                >
                  Privacy Policy
                </a>

                {" · "}

                <a
                  href="https://www.satstacker.app/terms"
                  className="hover:underline"
                >
                  Terms of Service
                </a>
              </div>
            </div>
          </div>
          {/* Pre-auth education popup */}
          {showPreAuthEdu && (
            <PreAuthEduPopup
              step={preAuthStep}
              setStep={setPreAuthStep}
              onDismiss={dismissPreAuthEdu}
              onGoCalculator={() => {
                dismissPreAuthEdu();
                afTrack("calculator_cta_tapped", { entry: "preauth_edu" });
                navigate("/calculator");
              }}
            />
          )}
        </>
      )}

      {/* Strike link warning modal */}
      {showStrikeLinkWarning && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm px-4 pb-6">
          <div className="w-full max-w-sm bg-zinc-900 border border-zinc-700 rounded-2xl p-6 shadow-xl">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setShowStrikeLinkWarning(false)}
                className="text-zinc-500 hover:text-zinc-300 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="flex flex-col items-center text-center mb-5">
              <p className="text-lg font-bold text-white">Linking your Strike account</p>
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed mb-6">
              You'll be taken to a browser window to sign in to Strike and approve access. Once done, come back to SatStacker and your plan will activate automatically.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowStrikeLinkWarning(false);
                handleConnectStrike();
              }}
              className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-bold text-sm mb-3 transition"
            >
              Got it, let’s go →
            </button>
            <button
              type="button"
              onClick={() => setShowStrikeLinkWarning(false)}
              className="w-full py-2 text-xs text-zinc-500 hover:text-zinc-300 transition"
            >
              Not right now
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------- PriceChart ---------- */
function PriceChart({ btcPrice, jwt, apiBase, planFreq, userId }) {
  const [range, setRange] = React.useState("1m");
  const [chartData, setChartData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const canvasRef = React.useRef(null);
  const chartRef = React.useRef(null);
  const [customTooltip, setCustomTooltip] = React.useState(null);
  // customTooltip: { x, y, cluster } | null
  const [retryCount, setRetryCount] = React.useState(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 3000;
  const [windowLines, setWindowLines] = React.useState([]);

  React.useEffect(() => {
    if (!userId) {
      setChartData(null);
      setLoading(false);
      return;
    }

    // Always read fresh from storage — don't rely on jwt prop timing
    const token =
      getApiAccessToken() ||
      jwt;
    if (!token) {
      setChartData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${apiBase}/me/price-history?range=${range}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (!cancelled) {
          setChartData(data);
          setRetryCount(0); // reset on success
        }
      })
      .catch((e) => {
        if (!cancelled) {
          if (retryCount < MAX_RETRIES) {
            // Auto-retry after a short delay — handles race conditions on
            // first load after an app update where the token/server may not
            // be fully ready on the initial mount tick.
            setTimeout(() => {
              if (!cancelled) setRetryCount((c) => c + 1);
            }, RETRY_DELAY_MS);
          } else {
            setError(e.message); // only show error after all retries exhausted
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [range, userId, apiBase, retryCount]);

  React.useEffect(() => {
    // If chartData is cleared (logout/user switch), destroy the chart immediately
    if (!chartData?.prices?.length) {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      setWindowLines([]);
      return;
    }

    if (!canvasRef.current) return;

    // Destroy previous instance before rebuilding
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const prices = chartData.prices;
    const trades = chartData.trades || [];

    const labels = prices.map((p) => p.t);
    const priceValues = prices.map((p) => ({ x: p.t, y: p.p }));

    // Build trade dot datasets — snap each trade to its nearest bucket label
    // Suppress dots based on cadence + range to avoid crowding:
    // daily → dots on 1W only
    // weekly/bi-weekly → dots on 1W and 1M only
    const isDaily = planFreq === "daily";
    const isWeekly = planFreq === "weekly";
    const isBiWeekly = planFreq === "bi-weekly" || planFreq === "biweekly" || (!isDaily && !isWeekly);
    const showDots =
      chartData.range === "1w" ||
      (chartData.range === "1m" && !isDaily) ||
      (chartData.range === "3m" && isBiWeekly);
    const visibleTrades = showDots ? trades : [];

    // Pre-build a sorted array of {ms, label, price} for fast nearest lookup
    const priceLookup = prices.map((p) => ({
      ms: new Date(p.t).getTime(),
      label: p.t,
      price: p.p,
    })).sort((a, b) => a.ms - b.ms);

    // Snap each trade to nearest price bucket label
    const snappedTrades = visibleTrades.map((t) => {
      const tradeMs = new Date(t.t).getTime();
      let best = priceLookup[0];
      let bestDiff = Infinity;
      for (const entry of priceLookup) {
        const diff = Math.abs(entry.ms - tradeMs);
        if (diff < bestDiff) { bestDiff = diff; best = entry; }
        else if (entry.ms > tradeMs + bestDiff) break;
      }
      return { ...t, label: best.label, price: best.price };
    });

    // Cluster trades that share the same label into one dot
    // meta.trades holds all trades in that cluster for the tooltip
    const clusterMap = {};
    snappedTrades.forEach((t) => {
      if (!clusterMap[t.label]) {
        clusterMap[t.label] = {
          label: t.label,
          price: t.price,
          smart: t.smart,
          trades: [],
        };
      }
      clusterMap[t.label].trades.push(t);
      // If any trade in cluster is smart, mark cluster as smart
      if (t.smart) clusterMap[t.label].smart = true;
    });

    const smartDots = [];
    const standardDots = [];
    Object.values(clusterMap).forEach((cluster) => {
      const dot = {
        x: cluster.label,
        y: cluster.price,
        meta: cluster, // cluster.trades is the array
        count: cluster.trades.length,
      };
      if (cluster.smart) smartDots.push(dot);
      else standardDots.push(dot);
    });

    const hasSmartTrades = visibleTrades.some((t) => t.smart);
    const nowMs = Date.now();
    const chartStartMs = priceLookup.length ? priceLookup[0].ms : 0;

    const windowStartIsos = showDots && hasSmartTrades
      ? Array.from(new Set([
          ...(chartData.current_window_start
            && new Date(chartData.current_window_start).getTime() <= nowMs
            && new Date(chartData.current_window_start).getTime() >= chartStartMs
            ? [chartData.current_window_start]
            : []),
          ...visibleTrades.map((t) => t.ws).filter(Boolean),
        ]))
      : [];

    // Snap each window_start to nearest price bucket label
    const computedWindowLines = windowStartIsos.map((iso) => {
      const ms = new Date(iso).getTime();
      let best = priceLookup[0];
      let bestDiff = Infinity;
      for (const entry of priceLookup) {
        const diff = Math.abs(entry.ms - ms);
        if (diff < bestDiff) { bestDiff = diff; best = entry; }
        else if (entry.ms > ms + bestDiff) break;
      }
      // Exclude window lines whose source timestamp predates the chart's visible range
      if (ms < chartStartMs) return null;
      return best.label;
    }).filter(Boolean);
    setWindowLines(computedWindowLines);

    const windowLinesPlugin = {
      id: "windowLines",
      afterDraw(chart) {
        if (!computedWindowLines.length) return;
        const { ctx, scales: { x, y } } = chart;
        const top = y.top;
        const bottom = y.bottom;

        ctx.save();
        ctx.setLineDash([3, 4]);
        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgba(161,161,170,0.4)"; // gray — structural reference, not hero element

        computedWindowLines.forEach((label) => {
          const xPixel = x.getPixelForValue(label);
          if (xPixel == null || isNaN(xPixel)) return;
          ctx.beginPath();
          ctx.moveTo(xPixel, top);
          ctx.lineTo(xPixel, bottom);
          ctx.stroke();
        });

        ctx.restore();
      },
    };

    const ctx = canvasRef.current.getContext("2d");

    chartRef.current = new ChartJS(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "BTC Price",
            data: priceValues,
            parsing: false,
            borderColor: "rgba(255,255,255,0.7)",
            backgroundColor: "rgba(255,255,255,0.04)",
            borderWidth: 1.5,
            pointRadius: 0,
            tension: 0.3,
            fill: true,
            order: 3,
          },
          {
            label: "Smart Timing buy",
            data: smartDots,
            type: "scatter",
            pointRadius: (ctx) => {
              const count = ctx.raw?.count ?? 1;
              return count > 1 ? 8 : 6;
            },
            pointHoverRadius: (ctx) => {
              const count = ctx.raw?.count ?? 1;
              return count > 1 ? 11 : 9;
            },
            pointBackgroundColor: "rgba(249,115,22,1)",
            pointBorderColor: "#000",
            pointBorderWidth: 1.5,
            showLine: false,
            order: 1,
          },
          {
            label: "Standard buy",
            data: standardDots,
            type: "scatter",
            pointRadius: (ctx) => {
              const count = ctx.raw?.count ?? 1;
              return count > 1 ? 7 : 5;
            },
            pointHoverRadius: (ctx) => {
              const count = ctx.raw?.count ?? 1;
              return count > 1 ? 10 : 8;
            },
            pointBackgroundColor: "rgba(161,161,170,1)",
            pointBorderColor: "#000",
            pointBorderWidth: 1.5,
            showLine: false,
            order: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "nearest", axis: "x", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false, // fully disabled — custom React overlay handles all tooltips
            callbacks: {
              title: (items) => {
                const raw = items[0]?.raw;
                if (raw?.meta?.trades?.length) {
                  // Show date from first trade in cluster
                  const d = new Date(raw.meta.trades[0].t);
                  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
                }
                const d = new Date(items[0]?.label);
                return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
              },
              label: (item) => {
                const raw = item.raw;
                if (raw?.meta?.trades?.length) {
                  const cluster = raw.meta;
                  const trades = cluster.trades;
                  const totalUsd = trades.reduce((s, t) => s + t.usd, 0);
                  const totalSats = trades.reduce((s, t) => s + t.sats, 0);
                  const allSmart = trades.every((t) => t.smart);
                  const tag = allSmart ? "⚡ Smart Timing" : "• Standard";
                  const countLabel = trades.length > 1 ? `${trades.length} buys` : "buy";
                  return `${tag} · ${countLabel}: $${totalUsd.toFixed(2)} → ${totalSats.toLocaleString()} sats`;
                }
                const val = typeof item.raw === "object" ? item.raw?.y : item.raw;
                return `$${Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
              },
            },
            displayColors: false,
            backgroundColor: "#18181b",
            borderColor: "#3f3f46",
            borderWidth: 1,
            titleColor: "#fff",
            bodyColor: "#a1a1aa",
            padding: 10,
          },
        },
        scales: {
          x: {
            display: false,
          },
          y: {
            position: "right",
            grid: { color: "rgba(255,255,255,0.05)" },
            ticks: {
              color: "#71717a",
              maxTicksLimit: 4,
              callback: (v) => `$${(v / 1000).toFixed(0)}k`,
            },
            afterDataLimits: (scale) => {
              const range = scale.max - scale.min;
              scale.max += range * 0.08;
            },
          },
        },
      },
      plugins: [windowLinesPlugin],
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [chartData]);

  // Shared hit-test helper — checks dot datasets for proximity, returns closest cluster or null
  const findClosestDot = React.useCallback((chart, clientX, clientY, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const HIT_RADIUS = 11;
    let closest = null;
    let closestDist = Infinity;
    [1, 2].forEach((datasetIdx) => {
      const meta = chart.getDatasetMeta(datasetIdx);
      if (!meta?.data) return;
      meta.data.forEach((point, pointIdx) => {
        const dx = point.x - x;
        const dy = point.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < HIT_RADIUS && dist < closestDist) {
          closestDist = dist;
          const raw = chart.data.datasets[datasetIdx].data[pointIdx];
          closest = { canvasX: point.x, canvasY: point.y, cluster: raw?.meta };
        }
      });
    });
    return closest;
  }, []);

  // Helper: find the price at a given clientX position by scanning dataset 0
  const findPriceAtX = React.useCallback((chart, clientX, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return null;
    let closest = null;
    let closestDx = Infinity;
    meta.data.forEach((point, idx) => {
      const dx = Math.abs(point.x - x);
      if (dx < closestDx) {
        closestDx = dx;
        const raw = chart.data.datasets[0].data[idx];
        closest = { canvasX: point.x, canvasY: point.y, price: raw?.y ?? null, label: chart.data.labels[idx] };
      }
    });
    return closest;
  }, []);

  // Custom touch handler — mobile only
  // On touchstart/touchmove: show dot tooltip if near a dot, otherwise show price crosshair
  // On touchend: dismiss tooltip
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouch = (e) => {
      const chart = chartRef.current;
      if (!chart) return;

      // Mark touch active to suppress synthetic mouse events that follow on Android
      touchActiveRef.current = true;
      if (touchLockTimerRef.current) clearTimeout(touchLockTimerRef.current);

      const touch = e.touches[0];

      const dotHit = findClosestDot(chart, touch.clientX, touch.clientY, canvas);
      if (dotHit) {
        e.preventDefault();
        setCustomTooltip({ x: dotHit.canvasX, y: dotHit.canvasY, cluster: dotHit.cluster });
        return;
      }

      // No dot hit — show price crosshair tooltip
      e.preventDefault();
      const priceHit = findPriceAtX(chart, touch.clientX, canvas);
      if (priceHit && priceHit.price != null) {
        setCustomTooltip({ x: priceHit.canvasX, y: priceHit.canvasY, cluster: null, price: priceHit.price, label: priceHit.label });
      } else {
        setCustomTooltip(null);
      }
    };

    const handleTouchEnd = () => {
      setCustomTooltip(null);
      // Keep the lock on for 500ms to swallow the synthetic mousemove Android fires after touch
      touchLockTimerRef.current = setTimeout(() => {
        touchActiveRef.current = false;
      }, 500);
    };

    canvas.addEventListener("touchstart", handleTouch, { passive: false });
    canvas.addEventListener("touchmove", handleTouch, { passive: false });
    canvas.addEventListener("touchend", handleTouchEnd);
    canvas.addEventListener("touchcancel", handleTouchEnd);
    return () => {
      canvas.removeEventListener("touchstart", handleTouch);
      canvas.removeEventListener("touchmove", handleTouch);
      canvas.removeEventListener("touchend", handleTouchEnd);
      canvas.removeEventListener("touchcancel", handleTouchEnd);
      if (touchLockTimerRef.current) clearTimeout(touchLockTimerRef.current);
    };
  }, [chartData, findClosestDot, findPriceAtX]);

  // Touch-lock ref — suppresses synthetic mouse events fired by Android/iOS after touchend
  // Set to true on any touch interaction, cleared after a short delay once touch ends
  const touchActiveRef = React.useRef(false);
  const touchLockTimerRef = React.useRef(null);

  // Custom mouse handler — desktop only
  // Skipped entirely if a touch interaction just occurred (touchActiveRef guard)
  // On mousemove: show dot tooltip if hovering near a dot (Chart.js handles price hover natively)
  // On mouseleave: dismiss dot tooltip
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMouseMove = (e) => {
      // Ignore synthetic mouse events generated after touch on Android/iOS
      if (touchActiveRef.current) return;
      const chart = chartRef.current;
      if (!chart) return;
      const dotHit = findClosestDot(chart, e.clientX, e.clientY, canvas);
      if (dotHit) {
        setCustomTooltip({ x: dotHit.canvasX, y: dotHit.canvasY, cluster: dotHit.cluster });
      } else {
        setCustomTooltip(null);
      }
    };

    const handleMouseLeave = () => {
      if (touchActiveRef.current) return;
      setCustomTooltip(null);
    };

    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [chartData, findClosestDot]);

  // Price change across the currently selected chart range.
  // Uses the live BTC price as the ending price so the percentage
  // corresponds to the large price displayed in the card.
  const rangeChangePct = React.useMemo(() => {
    const points = Array.isArray(chartData?.prices)
      ? chartData.prices
          .map((p) => ({
            time: new Date(p?.t).getTime(),
            price: Number(p?.p),
          }))
          .filter(
            (p) =>
              Number.isFinite(p.time) &&
              Number.isFinite(p.price) &&
              p.price > 0
          )
          .sort((a, b) => a.time - b.time)
      : [];

    if (points.length < 2) return null;

    const startPrice = points[0].price;
    const livePrice = Number(btcPrice);

    const endPrice =
      Number.isFinite(livePrice) && livePrice > 0
        ? livePrice
        : points[points.length - 1].price;

    const change =
      ((endPrice - startPrice) / startPrice) * 100;

    // Prevent "-0.00%" from appearing.
    return Math.abs(change) < 0.005 ? 0 : change;
  }, [chartData?.prices, btcPrice]);

  const ranges = ["1w", "1m", "3m", "1y"];

  // Dismiss tooltip when range changes
  React.useEffect(() => { setCustomTooltip(null); }, [range]);

  return (
    <div className="ss-card ss-card-pad w-full relative">
      {/* Header — styled to match the SatStacker ad creative */}
      <div className="grid grid-cols-[36px_minmax(0,1fr)_auto] grid-rows-[auto_auto] items-center gap-x-2.5 gap-y-1 mb-3">

        {/* Bitcoin medallion */}
        <div
          className="row-span-2 flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-orange-500 shadow-sm shadow-black/25 ring-1 ring-orange-300/15"
          aria-hidden="true"
        >
          <span className="block text-[21px] font-black leading-none text-white rotate-[10deg] translate-x-[0.5px] translate-y-[0.5px]">
            ₿
          </span>
        </div>

        {/* Label */}
        <p className="ss-section-title self-end">
          BITCOIN PRICE
        </p>

        {/* Range toggle */}
        <div className="flex gap-1 justify-self-end">
          {ranges.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={`text-xs px-2.5 py-1 rounded-md font-medium transition ${
                range === r
                  ? "bg-zinc-700 text-white"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {r.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Current price */}
        <p className="text-2xl font-bold text-white tabular-nums self-start">
          {btcPrice ? (
            `$${Number(btcPrice).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          ) : (
            <span className="inline-block w-24 h-6 bg-zinc-700 rounded animate-pulse" />
          )}
        </p>

        {/* Change over selected range */}
        <div className="min-h-[18px] justify-self-end self-start">
          {rangeChangePct != null &&
            !loading &&
            (!chartData?.range || chartData.range === range) && (
              <div
                className={`flex items-center gap-1 text-xs font-bold tabular-nums ${
                  rangeChangePct > 0
                    ? "text-emerald-400"
                    : rangeChangePct < 0
                    ? "text-red-400"
                    : "text-zinc-400"
                }`}
                aria-label={`${range.toUpperCase()} Bitcoin price change ${rangeChangePct.toFixed(
                  2
                )} percent`}
              >
                <span aria-hidden="true" className="text-[10px]">
                  {rangeChangePct > 0
                    ? "▲"
                    : rangeChangePct < 0
                    ? "▼"
                    : "•"}
                </span>

                <span>
                  {rangeChangePct > 0 ? "+" : ""}
                  {rangeChangePct.toFixed(2)}%
                </span>
              </div>
            )}
        </div>
      </div>

      {/* Chart area */}
      <div className="relative h-40">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full h-full bg-zinc-800/40 rounded animate-pulse" />
          </div>
        )}
        {error && !loading && chartData === null && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-zinc-500">
            Could not load price data
          </div>
        )}
        <canvas ref={canvasRef} />
        {/* Custom tooltip overlay — matches Chart.js built-in style */}
        {customTooltip && (() => {
          const CHART_W = canvasRef.current?.offsetWidth || 300;
          const rawX = customTooltip.x;
          const rawY = customTooltip.y;
          const isDot = !!customTooltip.cluster;

          // Dot: 80px above to clear thumb; flip to 36px below if near top edge
          // Price crosshair: tracks rawY, 90px above touch to clear thumb + line
          const aboveThumb = rawY >= 80;
          const top = isDot
            ? (aboveThumb ? rawY - 80 : rawY + 36)
            : Math.max(4, rawY - 90);

          // Horizontal: anchor by right edge in right half so tooltip extends
          // leftward and stays fully on screen. Left half anchors normally.
          const isRightHalf = rawX > CHART_W / 2;
          const hPos = isRightHalf
            ? { right: CHART_W - rawX - 10 }
            : { left: Math.max(4, rawX - 10) };

          // Stem line: connects tooltip box to the dot so it's clear which dot
          // triggered it when the tooltip is far away. Only for dot tooltips.
          // Runs from bottom of tooltip box (top + ~52px) down to the dot (rawY),
          // or from top of tooltip box up to dot when flipped below.
          const TOOLTIP_H = 52; // approximate rendered height of the 3-line dot tooltip
          const stemTop = aboveThumb ? top + TOOLTIP_H : rawY + 8;
          const stemHeight = aboveThumb ? rawY - top - TOOLTIP_H : top - rawY - 8;
          const stemStyle = {
            position: "absolute",
            left: rawX - 0.5,
            top: stemTop,
            width: 1,
            height: Math.max(0, stemHeight),
            background: "rgba(249,115,22,0.5)", // faint orange to match dot color
            pointerEvents: "none",
            zIndex: 19,
          };

          // Shared container style — identical to Chart.js defaults
          const containerStyle = {
            position: "absolute", top, ...hPos,
            background: "#18181b", border: "1px solid #3f3f46",
            borderRadius: 6, padding: "5px 8px", pointerEvents: "none",
            zIndex: 20, boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
            fontFamily: "inherit", whiteSpace: "nowrap",
          };
          const titleStyle = { color: "#ffffff", fontSize: 11, fontWeight: 600, marginBottom: 3, lineHeight: "1.4" };
          const bodyStyle  = { color: "#a1a1aa", fontSize: 11, lineHeight: "1.5", margin: 0 };

          // Dot tooltip (trade cluster)
          if (customTooltip.cluster) {
            const cluster = customTooltip.cluster;
            const trades = cluster.trades || [];
            const totalUsd = trades.reduce((s, t) => s + t.usd, 0);
            const totalSats = trades.reduce((s, t) => s + t.sats, 0);
            const allSmart = trades.every((t) => t.smart);
            const tag = allSmart ? "⚡ Smart Timing" : "• Standard";
            const countLabel = trades.length > 1 ? `${trades.length} buys` : "buy";
            const firstTrade = trades[0];
            const dateStr = firstTrade
              ? new Date(firstTrade.t).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
              : "";
            return (
              <>
                <div style={stemStyle} />
                <div style={containerStyle}>
                  <p style={titleStyle}>{dateStr}</p>
                  <p style={bodyStyle}>{tag} · {countLabel}</p>
                  <p style={{ ...bodyStyle, color: "#e4e4e7" }}>${totalUsd.toFixed(2)} → {totalSats.toLocaleString()} sats</p>
                </div>
              </>
            );
          }

          // Price crosshair tooltip (touch on price line, mobile only)
          if (customTooltip.price != null) {
            const dateStr = customTooltip.label
              ? new Date(customTooltip.label).toLocaleDateString(undefined, { month: "short", day: "numeric" })
              : "";
            return (
              <div style={containerStyle}>
                <p style={titleStyle}>{dateStr}</p>
                <p style={bodyStyle}>${Number(customTooltip.price).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
              </div>
            );
          }

          return null;
        })()}
      </div>

      {/* Legend — only if there are trades and dots are visible */}
      {chartData?.trades?.length > 0 &&
        (chartData.range === "1w" ||
         (chartData.range === "1m" && planFreq !== "daily") ||
         (chartData.range === "3m" && (planFreq === "bi-weekly" || planFreq === "biweekly" || (planFreq !== "daily" && planFreq !== "weekly")))) && (
        <div className="flex gap-4 mt-2 text-[11px] text-zinc-500">
          {chartData.trades.some((t) => t.smart) && (
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block" />
              Smart Timing buy
            </span>
          )}
          {chartData.trades.some((t) => !t.smart) && (
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-zinc-500 inline-block" />
              Standard buy
            </span>
          )}
          {windowLines.length > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 border-t border-dashed border-zinc-500/60" style={{ borderTopWidth: 1.5 }} />
              Window close
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- small reusable components ---------- */
function Card({ title, value, subtext, icon, accent = "none" }) {
  return (
    <div className={`ss-card ss-card-pad w-full ${accent === "none" ? "" : "relative"}`}>
      {accent === "left" && <div className="absolute left-0 top-0 h-full w-1 bg-orange-500 rounded-l-xl" />}
      <div className="flex items-center space-x-4">
        {icon && <div className="flex-shrink-0 p-3 rounded-full bg-white/5 border border-border">{icon}</div>}
        <div>
          <p className="ss-section-title">{title}</p>
          <p className="text-2xl font-bold text-white tabular-nums">{value}</p>
          {subtext && <p className="text-sm text-textMuted">{subtext}</p>}
        </div>
      </div>
    </div>
  );
}

function ChartBox({ title, labels, data, color, yFmt }) {
  return (
    <div className="bg-card p-6 rounded-xl border border-border shadow-card">
      <h4 className="font-semibold mb-4">{title}</h4>
      <Line
        data={{ labels, datasets: [{ data, borderColor: color, backgroundColor: color.replace('0.9', '0.1'), pointRadius: 3, pointHoverRadius: 5, pointBorderWidth: 0, borderWidth: 2, tension: 0.3, fill:true, }] }}
        options={{
          responsive: true,
          interaction: { mode: "index", axis: "x", intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              displayColors: false,
              callbacks: {
                label: (ctx) => (yFmt ? yFmt(ctx.parsed.y) : ctx.parsed.y),
              },
            },
          },
          scales: {
            y: {
              ticks: { callback: yFmt },
              grid: { color: "rgba(255,255,255,0.05)" },
            },
            x: {
              grid: { display: false },
              ticks: {
                color: "#71717a",
                maxTicksLimit: 5,
                maxRotation: 0,
                autoSkip: true,
                callback: function(val, idx, ticks) {
                  const label = this.getLabelForValue(val);
                  if (!label) return "";
                  const d = new Date(label);
                  if (isNaN(d)) return label;
                  // Deduplicate — only show label if date is different from previous tick
                  if (idx > 0) {
                    const prevLabel = this.getLabelForValue(ticks[idx - 1]?.value);
                    if (prevLabel) {
                      const prevD = new Date(prevLabel);
                      if (!isNaN(prevD) &&
                          prevD.getFullYear() === d.getFullYear() &&
                          prevD.getMonth() === d.getMonth() &&
                          prevD.getDate() === d.getDate()) {
                        return "";
                      }
                    }
                  }
                  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                },
              },
            },
          },
        }}
      />
    </div>
  );
}

function PreAuthEduPopup({ step, setStep, onDismiss, onGoCalculator }) {
  const CONTENT = {
    learn_btc: {
      icon: <span className="text-4xl font-black text-orange-400 leading-none">₿</span>,
      title: "What is Bitcoin?",
      body: "Bitcoin is digital money that no government or bank controls. Unlike dollars, no one can print more of it — only 21 million will ever exist. That fixed supply is written into the code and can never be changed.",
    },
    learn_btc_2: {
      icon: (
        <svg width="56" height="56" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* same paths, just wider/taller canvas renders it larger */}
          <path d="M14 20 V15 C14 9 30 9 30 15 V20" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
          <rect x="9" y="20" width="26" height="18" rx="4" stroke="#f97316" strokeWidth="2.5"/>
          <circle cx="22" cy="29" r="3" stroke="#f97316" strokeWidth="2"/>
          <line x1="22" y1="32" x2="22" y2="36" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      title: "Why do people hold it?",
      body: "The dollar loses purchasing power every year through inflation. Bitcoin's supply is fixed, so it can't be inflated away. It's held by individuals, companies, and institutions as a long-term savings asset outside the traditional financial system.",
    },
    learn_dca: {
      icon: (
        <span className="inline-flex items-end gap-1 leading-none">
          {[10, 14, 18, 22, 26].map((h, i) => (
            <span
              key={i}
              className="inline-block w-3 rounded-sm bg-orange-400"
              style={{ height: `${h}px`, opacity: 0.5 + i * 0.12 }}
            />
          ))}
        </span>
      ),
      title: "What is DCA?",
      body: "Instead of trying to pick the perfect moment to buy, you invest a fixed amount on a regular schedule — say $50 every week, no matter the price. Some weeks you buy high, some weeks low, but your average cost smooths out over time. No stress. No guessing.",
    },
    learn_satstacker: {
      icon: <span className="text-4xl leading-none">🟠</span>,
      title: "What is a sat?",
      body: "A sat (short for satoshi) is the smallest unit of Bitcoin, like a penny to a dollar. One Bitcoin equals 100 million sats. SatStacker tracks your stack in sats so you can see every fraction you earn, even when buying small amounts.",
    },
    learn_satstacker_2: {
      icon: <span className="text-4xl leading-none">⚡</span>,
      title: "How SatStacker helps",
      body: "SatStacker automates your Bitcoin DCA through Strike. Smart Timing is SatStacker's buying algorithm. It looks for better Bitcoin prices inside each buy window while keeping your recurring budget and cadence the same. Same schedule. Same budget. Smarter timing.",
    },
  };

  const renderBody = () => {
    // Question screens
    if (step === "ask_btc") {
      return (
        <>
          <p className="text-lg font-bold text-white mb-1">Do you know what Bitcoin is?</p>
          <p className="text-sm text-zinc-400 mb-6">Help us skip what you already know.</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => { afTrack("preauth_edu_answer", { q: "btc", a: "yes" }); setStep("ask_dca"); }}
              className="w-full py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white font-semibold text-sm"
            >
              Yes, I know Bitcoin
            </button>
            <button
              onClick={() => { afTrack("preauth_edu_answer", { q: "btc", a: "no" }); setStep("learn_btc"); }}
              className="w-full py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white font-semibold text-sm"
            >
              Not really, explain it
            </button>
          </div>
        </>
      );
    }

    if (step === "ask_dca") {
      return (
        <>
          <p className="text-lg font-bold text-white mb-1">Do you know what DCA is?</p>
          <p className="text-sm text-zinc-400 mb-6">Dollar Cost Averaging — the strategy behind SatStacker.</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => { afTrack("preauth_edu_answer", { q: "dca", a: "yes" }); setStep("learn_satstacker_2"); }}
              className="w-full py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white font-semibold text-sm"
            >
              Yes, I DCA already
            </button>
            <button
              onClick={() => { afTrack("preauth_edu_answer", { q: "dca", a: "no" }); setStep("learn_dca"); }}
              className="w-full py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white font-semibold text-sm"
            >
              No, what is it?
            </button>
          </div>
        </>
      );
    }

    // Education + continuation screens
    const slide = CONTENT[step];
    if (!slide) return null;

    const isLastBeforeCalc = step === "learn_satstacker_2";
    const nextStep =
      step === "learn_btc"         ? "learn_btc_2" :
      step === "learn_btc_2"       ? "learn_dca" :
      step === "learn_dca"         ? "learn_satstacker" :
      step === "learn_satstacker"  ? "learn_satstacker_2" :
      null;

    return (
      <>
        <div className="flex flex-col items-center text-center mb-5">
          <div className="mb-3">{slide.icon}</div>
          <p className="text-lg font-bold text-white">{slide.title}</p>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed mb-6">{slide.body}</p>
        {isLastBeforeCalc ? (
          <button
            onClick={() => { afTrack("preauth_edu_cta", { step }); onGoCalculator(); }}
            className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-bold text-sm transition"
          >
            See how much more Bitcoin you'd stack →
          </button>
        ) : (
          <button
            onClick={() => setStep(nextStep)}
            className="w-full py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white font-semibold text-sm"
          >
            Got it, what's next?
          </button>
        )}
      </>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm px-4 pb-6">
      <div className="w-full max-w-sm bg-zinc-900 border border-zinc-700 rounded-2xl p-6 shadow-xl">
        {/* Close */}
        <div className="flex justify-end mb-2">
          <button
            onClick={onDismiss}
            className="text-zinc-500 hover:text-zinc-300 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {renderBody()}
      </div>
    </div>
  );
}

function SmartTimingPreviewSvg() {
  const uid = React.useId();
  const areaId = `previewArea-${uid.replace(/:/g, "")}`;

  // Curated representative 4-week BTC-like price path. Not live data.
  // Shaped like a real month: one dominant swing, minor wiggles, imperfect buys.
  const prices = [
    100.0, 100.4, 99.7, 100.2, 100.9, 101.4, 100.6,   // W1: drift up, no dip
    101.9, 100.6, 99.2, 99.8, 98.3, 97.1, 97.6,        // W2: decline
    96.8, 95.9, 94.6, 94.1, 94.9, 95.8, 96.6,          // W3: trough
    97.4, 96.9, 95.3, 96.8, 98.1, 99.0, 99.6, 100.2,   // W4: recovery
  ];

  const W = 360;
  const H = 190;
  const PAD = { l: 18, r: 18, t: 16, b: 30 };
  const chartW = W - PAD.l - PAD.r;
  const chartH = H - PAD.t - PAD.b;

  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;

  const x = (i) => PAD.l + (i / (prices.length - 1)) * chartW;
  const y = (v) => PAD.t + (1 - (v - min) / range) * chartH;

  const pts = prices.map((p, i) => ({ i, price: p, x: x(i), y: y(p) }));

  const lineD = pts
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");

  const areaD =
    `${lineD} L ${x(prices.length - 1).toFixed(1)} ${H - PAD.b}` +
    ` L ${x(0).toFixed(1)} ${H - PAD.b} Z`;

  // Gray dots show scheduled DCA at the end of each weekly buy window.
  // Orange dots illustrate Smart Timing entries inside those windows.
  const weekStarts   = [0, 7, 14, 21];
  const scheduledIdx = [7, 14, 21, 28];
  const smartIdx     = [5, 11, 17, 24];

  const fixed = scheduledIdx.map((i) => pts[i]);
  const smart = smartIdx.map((i) => pts[i]);

  return (
    <div className="rounded-2xl border border-zinc-800 bg-[#0b0b0d] p-3">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-[190px] w-full"
        aria-label="Example Bitcoin price chart comparing scheduled DCA buys and Smart Timing buys"
      >
        <defs>
          <linearGradient id={areaId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#f97316" stopOpacity="0.14" />
            <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* subtle horizontal grid */}
        {[0.25, 0.5, 0.75].map((t) => {
          const yy = PAD.t + chartH * t;
          return (
            <line
              key={t}
              x1={PAD.l}
              y1={yy}
              x2={W - PAD.r}
              y2={yy}
              stroke="#27272a"
              strokeWidth="1"
              opacity="0.75"
            />
          );
        })}

        {/* weekly windows */}
        {weekStarts.map((i, idx) => (
          <g key={i}>
            <line
              x1={x(i)}
              y1={PAD.t}
              x2={x(i)}
              y2={H - PAD.b}
              stroke="#27272a"
              strokeWidth="1"
              strokeDasharray="3 6"
              opacity="0.9"
            />
            <text
              x={x(i)}
              y={H - 9}
              textAnchor="middle"
              fontSize="10"
              fill="#52525b"
            >
              W{idx + 1}
            </text>
          </g>
        ))}

        {/* area + line */}
        <path d={areaD} fill={`url(#${areaId})`} />

        <path
          d={lineD}
          fill="none"
          stroke="#d4d4d8"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* faint scheduled-DCA-to-smart guide lines */}
        {fixed.map((f, i) => {
          const s = smart[i];
          return (
            <line
              key={`guide-${i}`}
              x1={f.x}
              y1={f.y}
              x2={s.x}
              y2={s.y}
              stroke="#f97316"
              strokeWidth="1"
              strokeOpacity="0.24"
              strokeDasharray="4 5"
            />
          );
        })}

        {/* scheduled DCA buys */}
        {fixed.map((p, i) => (
          <g key={`fixed-${i}`}>
            <circle cx={p.x} cy={p.y} r="5" fill="#71717a" />
            <circle
              cx={p.x}
              cy={p.y}
              r="8"
              fill="none"
              stroke="#a1a1aa"
              strokeOpacity="0.25"
            />
          </g>
        ))}

        {/* smart buys */}
        {smart.map((p, i) => (
          <g key={`smart-${i}`}>
            <circle cx={p.x} cy={p.y} r="5.5" fill="#f97316" />
            <circle
              cx={p.x}
              cy={p.y}
              r="9"
              fill="none"
              stroke="#f97316"
              strokeOpacity="0.28"
            />
          </g>
        ))}
      </svg>
    </div>
  );
}